import DataTable, {Order} from "datatables.net-bs5";
import { Modal, Tooltip } from "bootstrap";
import * as Highcharts from "highcharts";
import 'highcharts/modules/drilldown';
import { getChartTheme } from './utils/chartTheme';
// v12: drilldown self-registers on side-effect import.
// 'animateDrilldown' on the pie prototype is the module-registration sentinel (D-01 loud guard).
if (typeof (Highcharts as any).seriesTypes?.['pie']?.prototype?.animateDrilldown === 'undefined') {
    console.error('[drilldown] module failed to register — pie drilldown will not work. Check the highcharts/modules/drilldown side-effect import.');
}
import {get, notifier, number_format, on, select_choices} from "./util";
import { groupColor } from './constants';
import { makeEmptyAwareTooltip } from './utils/emptyAwareTooltip';
import { initImportModal } from './import-modal';

// 281-REVIEW.md IN-07 (281-18, gap closure) — the delay between a successful Rule Library
// re-apply's success toast and the full page reload that follows it (IN-10). Long enough for the
// operator to read the toast; short enough that the page does not feel hung.
const TOAST_THEN_RELOAD_MS = 700;

/**
 * Local escape helper — same module-private copy shape as tracking.ts/workouts.ts
 * (no shared export exists in this repo to import instead).
 *
 * Required by renderSplitChildren(): split descriptions are operator free-text since the
 * split builder gained a Description field, and they are interpolated into a template
 * literal that goes through innerHTML.
 */
function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// Phase 181: the global `theme` string ternary pattern removed — chart chrome now via getChartTheme()
declare var txn_donut: any[];
declare var txn_monthly: any[];
declare var txn_monthly_by_group: Record<string, any[]>;
declare var txn_years: string[];
declare var txn_payload: Record<string, {
    by_group: { group: string; total: number }[];
    monthly_by_group: Record<string, { month: string; total: number; count: number }[]>;
    by_main: { main_id: number; main_name: string; main_color: string; total: number }[];
    monthly_by_main: Record<string, { month: string; total: number; count: number }[]>;
    by_sub_top10: { sub_id: number; sub_name: string; main_id: number; main_color: string; total: number }[];
    monthly_by_sub_top10: Record<string, { month: string; total: number; count: number }[]>;
    stats: any;
}>;
declare var txn_current_year: string;

// Phase 156: merchant deep-dive page-injected vars (set by merchant.latte page_js block)
declare var merchant_trend: { month: string; total: number; count: number }[];
declare var merchant_breakdown: { main_id: number; main_name: string; main_color: string; sub_id: number; sub_name: string; total: number }[];
declare var merchant_stats: any;
declare var merchant_key: string;

export default class {
    static init = () => {
        list();
        initQuarantinePanel();
        initImportModal({
            onSuccess: (_result) => {
                if (datatable) datatable.ajax.reload();
            }
        });
        undoButtons();
        stats();
        review();
        initRecipients();
        initTransfersPage();
        initRecurring();
        initMerchant();
        amazonAutoMatches();
        paypalDataTables();
        splitCategoryModal();
        txnCategoryModal();
        initSplitBuilderModal();
    }
}

// Phase 157 — client-side DataTable for the Amazon "Recent Auto-Matches" section so the
// operator can page/search/sort the full match history (back to 2020), not just the latest 50.
// Guarded to the amazon page by the data-table presence check. Unsplit reloads the page on
// success (no DOM row removal), so the DataTable never desyncs. The table inits while its
// accordion is collapsed (hidden) → autoWidth:false + columns.adjust() on show avoids header drift.
function amazonAutoMatches() {
    const table = document.querySelector('.datatable[data-table="amazon_auto_matches"]') as HTMLElement | null;
    if (!table) return;
    const dt = new DataTable(table, {
        pageLength: 25,
        lengthMenu: [10, 25, 50, 100],
        order: [[0, 'desc']] as unknown as Order,
        autoWidth: false,
        columnDefs: [
            { targets: 2, orderable: false },                    // Items (bullet breakdown) — searchable, not sortable
            { targets: 3, orderable: false, searchable: false }, // Action (Unsplit)
        ],
    });
    const section = document.getElementById('amz-sec-matches');
    if (section) {
        section.addEventListener('shown.bs.collapse', () => dt.columns.adjust());
    }
}

// Phase 160 — client-side DataTables for the PayPal de-obfuscation page.
// Two tables: paypal_auto_matches (Recent Auto-Matches sub-accordion) and
// paypal_transactions (all PayPal rows, Region B — server-rendered synchronously, NOT deferred).
// Page-guarded: returns early if neither table is present (this module loads on every
// Transactions page — guard prevents double-bind and errors on non-PayPal pages).
// Both tables init while collapsed → autoWidth:false + columns.adjust() on show avoids header drift.
// Default order: Date descending (column 0, 'desc') for both tables.
// No server-side ajax source — Region B is synchronously server-rendered (UI-SPEC :482-488).
function paypalDataTables() {
    const matchesTable  = document.querySelector('.datatable[data-table="paypal_auto_matches"]') as HTMLElement | null;
    const allTxnTable   = document.querySelector('.datatable[data-table="paypal_transactions"]') as HTMLElement | null;
    if (!matchesTable && !allTxnTable) return;  // guard: paypal page only

    // Recent Auto-Matches table (sub-accordion inside Region A)
    if (matchesTable) {
        const dtMatches = new DataTable(matchesTable, {
            pageLength: 25,
            lengthMenu: [10, 25, 50, 100],
            order: [[0, 'desc']] as unknown as Order,
            autoWidth: false,
            columnDefs: [
                { targets: 2, orderable: false, searchable: false }, // Action (Unsplit)
            ],
        });
        const matchesSection = document.getElementById('pp-sec-matches');
        if (matchesSection) {
            matchesSection.addEventListener('shown.bs.collapse', () => dtMatches.columns.adjust());
        }
    }

    // All PayPal Transactions table (Region B — client-side paged, 1,274 rows manageable)
    if (allTxnTable) {
        const dtAll = new DataTable(allTxnTable, {
            pageLength: 25,
            lengthMenu: [10, 25, 50, 100],
            order: [[0, 'desc']] as unknown as Order,
            autoWidth: false,
            columnDefs: [
                { targets: 5, orderable: false, searchable: false }, // Category (no meaningful sort)
            ],
        });
        const allSection = document.getElementById('pp-sec-all');
        if (allSection) {
            allSection.addEventListener('shown.bs.collapse', () => dtAll.columns.adjust());
        }
    }
}

// Phase 157 UAT — inline recategorize modal for Amazon split items on the /transactions list.
// Each split item's category cell is a .js-split-cat-edit trigger; clicking opens a small modal
// with a grouped category/sub-cat <select> (built from the injected categoryTree), pre-selected to
// the item's current sub-category. Save POSTs to handleSetSplitCategory and reloads so the
// split-detail cell AND the v_effective_amounts-based home/stats aggregates reflect the change.
function splitCategoryModal() {
    const modalEl = document.getElementById('splitCatModal');
    if (!modalEl) return; // list page only

    const categoryTree: { id: number; name: string; subs: { id: number; name: string }[] }[] =
        (window as any).categoryTree || [];
    const saveUrl = modalEl.getAttribute('data-save-url') || '';
    // Use the imported Modal — this app never assigns bootstrap to window, so
    // (window as any).bootstrap is undefined (the home modals use the import too).
    const modal = Modal.getOrCreateInstance(modalEl);

    const selectContainer = modalEl.querySelector('.js-split-cat-select-container') as HTMLElement | null;
    const itemLabel       = modalEl.querySelector('.js-split-cat-item') as HTMLElement | null;
    const errEl           = modalEl.querySelector('.js-split-cat-error') as HTMLElement | null;
    const saveBtn         = modalEl.querySelector('.js-split-cat-save') as HTMLButtonElement | null;

    let currentSplitId = '';
    let currentBtn: HTMLElement | null = null;

    function buildSelect(currentSubCatId: number): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm js-split-cat-select';
        categoryTree.forEach(main => {
            const og = document.createElement('optgroup');
            og.label = main.name;
            main.subs.forEach(sub => {
                const opt = document.createElement('option');
                opt.value = String(sub.id);
                opt.textContent = sub.name;
                if (sub.id === currentSubCatId) opt.selected = true;
                og.appendChild(opt);
            });
            sel.appendChild(og);
        });
        return sel;
    }

    // Delegated — split child rows render dynamically on expand.
    document.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.js-split-cat-edit') as HTMLElement | null;
        if (!btn) return;
        currentSplitId = btn.dataset.splitId || '';
        currentBtn = btn;
        const subCatId = parseInt(btn.dataset.subCatId || '0', 10);
        const desc = (btn.closest('tr')?.querySelector('td')?.textContent || '').trim();
        if (itemLabel) itemLabel.textContent = desc;
        if (selectContainer) { selectContainer.innerHTML = ''; selectContainer.appendChild(buildSelect(subCatId)); }
        if (errEl) errEl.classList.add('d-none');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
        modal?.show();
    });

    if (saveBtn) {
        saveBtn.addEventListener('click', () => {
            const sel = modalEl.querySelector('.js-split-cat-select') as HTMLSelectElement | null;
            const newSubCatId = sel?.value || '';
            if (!currentSplitId || !newSubCatId) return;
            saveBtn.disabled = true;
            saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
            if (errEl) errEl.classList.add('d-none');
            fetch(saveUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: 'splitId=' + encodeURIComponent(currentSplitId) + '&subCatId=' + encodeURIComponent(newSubCatId),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    const newName = sel?.selectedOptions[0]?.textContent || '';
                    const newId   = parseInt(newSubCatId, 10);
                    // 1) Update the clicked cell's badge + data attr in place (no reload).
                    if (currentBtn) {
                        const badge = currentBtn.querySelector('.badge');
                        if (badge) badge.textContent = newName;
                        currentBtn.dataset.subCatId = newSubCatId;
                    }
                    // 2) Patch the cached DataTable row data so collapse/re-expand stays
                    //    consistent — no ajax.reload(), no full redraw.
                    if (datatable && currentSplitId) {
                        datatable.rows().every(function (this: any) {
                            const d = this.data();
                            if (d && Array.isArray(d.splits)) {
                                const sp = d.splits.find((s: any) => String(s.id) === currentSplitId);
                                if (sp) { sp.sub_cat_id = newId; sp.sub_cat_name = newName; }
                            }
                        });
                    }
                    modal.hide();
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                } else {
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                    if (errEl) { errEl.textContent = data.message || 'Could not update.'; errEl.classList.remove('d-none'); }
                }
            })
            .catch(() => {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
                if (errEl) { errEl.textContent = 'Network error. Try again.'; errEl.classList.remove('d-none'); }
            });
        });
    }
}

// 158 UAT — recategorize a top-level transaction from the /transactions list Category column.
// Click a .js-txn-cat-edit cell → modal with the grouped categoryTree <select> pre-selected to
// the row's sub_cat_id → Save POSTs txnId + subCatId to handleSetTransactionCategory, then reloads.
// Mirrors splitCategoryModal() (split items); split PARENTS aren't clickable (server also refuses).
function txnCategoryModal() {
    const modalEl = document.getElementById('txnCatModal');
    if (!modalEl) return; // list page only

    const categoryTree: { id: number; name: string; subs: { id: number; name: string }[] }[] =
        (window as any).categoryTree || [];
    const saveUrl = modalEl.getAttribute('data-save-url') || '';
    const modal = Modal.getOrCreateInstance(modalEl);

    const selectContainer = modalEl.querySelector('.js-txn-cat-select-container') as HTMLElement | null;
    const itemLabel       = modalEl.querySelector('.js-txn-cat-item') as HTMLElement | null;
    const errEl           = modalEl.querySelector('.js-txn-cat-error') as HTMLElement | null;
    const saveBtn         = modalEl.querySelector('.js-txn-cat-save') as HTMLButtonElement | null;

    let currentTxnId = '';

    function buildSelect(currentSubCatId: number): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm js-txn-cat-select';
        categoryTree.forEach(main => {
            const og = document.createElement('optgroup');
            og.label = main.name;
            main.subs.forEach(sub => {
                const opt = document.createElement('option');
                opt.value = String(sub.id);
                opt.textContent = sub.name;
                if (sub.id === currentSubCatId) opt.selected = true;
                og.appendChild(opt);
            });
            sel.appendChild(og);
        });
        return sel;
    }

    document.addEventListener('click', (e: Event) => {
        const link = (e.target as HTMLElement).closest('.js-txn-cat-edit') as HTMLElement | null;
        if (!link) return;
        e.preventDefault();
        currentTxnId = link.dataset.txnId || '';
        const subCatId = parseInt(link.dataset.subCatId || '0', 10);
        // Resolve the row's name from the DataTable for the modal label.
        const tr = link.closest('tr') as HTMLTableRowElement | null;
        const rowData: any = (tr && datatable) ? datatable.row(tr).data() : null;
        const desc = rowData ? ((rowData.name ?? '') + ' — $' + number_format(parseFloat(rowData.amount ?? '0'), 2)) : '';
        if (itemLabel) itemLabel.textContent = desc;
        if (selectContainer) { selectContainer.innerHTML = ''; selectContainer.appendChild(buildSelect(subCatId)); }
        if (errEl) errEl.classList.add('d-none');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
        modal?.show();
    });

    if (saveBtn) {
        saveBtn.addEventListener('click', () => {
            const sel = modalEl.querySelector('.js-txn-cat-select') as HTMLSelectElement | null;
            const newSubCatId = sel?.value || '';
            if (!currentTxnId || !newSubCatId) return;
            saveBtn.disabled = true;
            saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
            if (errEl) errEl.classList.add('d-none');
            fetch(saveUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: 'txnId=' + encodeURIComponent(currentTxnId) + '&subCatId=' + encodeURIComponent(newSubCatId),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    modal.hide();
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                    if (datatable) datatable.ajax.reload(null, false); // keep current page
                } else {
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                    if (errEl) { errEl.textContent = data.message || 'Could not update.'; errEl.classList.remove('d-none'); }
                }
            })
            .catch(() => {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
                if (errEl) { errEl.textContent = 'Network error. Try again.'; errEl.classList.remove('d-none'); }
            });
        });
    }
}

// Phase 158 D-01..D-07/D-10 — Manual split builder modal.
// Opens the #splitTxnModal for new splits (pre-filled with full amount + parent category)
// or edit mode (pre-loaded with existing child lines). Each line row has:
//   - a number input (.js-split-amount)
//   - a grouped category <select> built via buildSelect() (D-03 — reuses Phase 157 function)
//   - a "Fill remaining" button (.js-fill-remaining) (D-07)
//   - a remove-line "×" button (hidden when only one line remains)
// Live remaining indicator updates on every input event (integer-cents math — no float ==).
// Save POSTs txnId + lines JSON to data-save-url (new split) or data-edit-url (edit).
// Do NOT add a categoryTree injection here — already present in list.latte (no duplicate).
function splitBuilderModal(txnId: number, txnAmount: number, txnDesc: string, currentSubCatId: number, existingLines?: { amount: number; sub_cat_id: number; description?: string }[]): void {
    const modalEl = document.getElementById('splitTxnModal');
    if (!modalEl) return;

    const categoryTree: { id: number; name: string; subs: { id: number; name: string }[] }[] =
        (window as any).categoryTree || [];

    const saveUrl     = modalEl.getAttribute('data-save-url') || '';
    const editUrl     = modalEl.getAttribute('data-edit-url') || '';
    const isEdit      = !!(existingLines && existingLines.length > 0);
    const useUrl      = isEdit ? editUrl : saveUrl;

    // Use the imported Modal — not the window global (undefined in this project)
    const modal = Modal.getOrCreateInstance(modalEl);

    const titleEl       = modalEl.querySelector('.js-split-modal-title') as HTMLElement | null;
    const descEl        = modalEl.querySelector('.js-split-modal-desc') as HTMLElement | null;
    const linesContainer = modalEl.querySelector('#js-split-lines-container') as HTMLElement | null;
    const addLineBtn    = modalEl.querySelector('.js-add-split-line') as HTMLButtonElement | null;
    const remainingEl   = modalEl.querySelector('.js-remaining-indicator') as HTMLElement | null;
    const errorEl       = modalEl.querySelector('.js-split-modal-error') as HTMLElement | null;
    const saveBtn       = modalEl.querySelector('.js-split-save') as HTMLButtonElement | null;

    if (!linesContainer || !saveBtn) return;

    // Total in integer cents to avoid float drift (D-06 exact-to-the-penny)
    const totalCents = Math.round(txnAmount * 100);

    // Reset modal state
    if (titleEl) titleEl.textContent = isEdit ? 'Edit split' : 'Split transaction';
    if (descEl) descEl.textContent = txnDesc;
    if (errorEl) { errorEl.classList.add('d-none'); errorEl.textContent = ''; }
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save split'; }
    linesContainer.innerHTML = '';

    // Build a category <select> using the injected categoryTree (D-03 — same grouped main→sub select)
    function buildSplitSelect(subCatId: number): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm';
        // Default empty option
        const empty = document.createElement('option');
        empty.value = '';
        empty.textContent = '— Select category —';
        sel.appendChild(empty);
        categoryTree.forEach(main => {
            const og = document.createElement('optgroup');
            og.label = main.name;
            main.subs.forEach(sub => {
                const opt = document.createElement('option');
                opt.value = String(sub.id);
                opt.textContent = sub.name;
                if (sub.id === subCatId) opt.selected = true;
                og.appendChild(opt);
            });
            sel.appendChild(og);
        });
        return sel;
    }

    // Compute allocated cents from all .js-split-amount inputs
    function getAllocatedCents(): number {
        const inputs = linesContainer.querySelectorAll('.js-split-amount') as NodeListOf<HTMLInputElement>;
        let sum = 0;
        inputs.forEach(inp => {
            const v = parseFloat(inp.value);
            if (!isNaN(v) && v > 0) sum += Math.round(v * 100);
        });
        return sum;
    }

    // Update the remaining indicator and Save button state
    function updateRemaining(): void {
        const allocated = getAllocatedCents();
        const remaining = totalCents - allocated;
        if (!remainingEl) return;
        if (remaining === 0) {
            remainingEl.innerHTML = '<span class="text-success">Fully allocated — $0.00 remaining</span>';
        } else if (remaining > 0) {
            remainingEl.innerHTML = `<span class="text-muted">$${(remaining / 100).toFixed(2)} remaining</span>`;
        } else {
            remainingEl.innerHTML = `<span class="text-danger">Over-allocated by $${(Math.abs(remaining) / 100).toFixed(2)}</span>`;
        }
        // Disable Save when over-allocated
        if (saveBtn) saveBtn.disabled = remaining < 0;
        // Update Fill remaining button states — disable when remaining <= 0
        linesContainer.querySelectorAll('.js-fill-remaining').forEach((btn) => {
            (btn as HTMLButtonElement).disabled = remaining <= 0;
        });
    }

    // Create a split line row DOM element
    function createLineRow(amount: number | null, subCatId: number, index: number, description: string = ''): HTMLElement {
        const row = document.createElement('div');
        // flex-wrap so amount/description/category stack instead of overflowing at 320px.
        row.className = 'split-line-row d-flex flex-wrap gap-2 mb-2 align-items-center';
        row.dataset.lineIndex = String(index);

        const amtInput = document.createElement('input');
        amtInput.type = 'number';
        amtInput.min = '0.01';
        amtInput.step = '0.01';
        amtInput.className = 'form-control form-control-sm js-split-amount';
        amtInput.style.flex = '0 0 100px';
        if (amount !== null && amount > 0) amtInput.value = amount.toFixed(2);
        amtInput.placeholder = '0.00';
        amtInput.setAttribute('aria-label', 'Split line amount');
        amtInput.addEventListener('input', updateRemaining);

        // Per-line label written to txn_splits.description — the "Item" column of the
        // expanded split-detail table (Amazon/PayPal splits fill it from the importer;
        // manual splits had no input for it until now). VARCHAR(255) in the schema.
        const descInput = document.createElement('input');
        descInput.type = 'text';
        descInput.className = 'form-control form-control-sm js-split-desc';
        descInput.maxLength = 255;
        descInput.placeholder = 'Description (optional)';
        descInput.setAttribute('aria-label', 'Split line description');
        descInput.style.flex = '1 1 150px';
        descInput.value = description;

        const catContainer = document.createElement('div');
        catContainer.style.flex = '2 1 170px';
        catContainer.appendChild(buildSplitSelect(subCatId));

        const fillBtn = document.createElement('button');
        fillBtn.type = 'button';
        fillBtn.className = 'btn btn-outline-secondary btn-sm js-fill-remaining';
        fillBtn.style.minHeight = '34px';
        fillBtn.textContent = 'Fill remaining';
        fillBtn.addEventListener('click', () => {
            const allocated = getAllocatedCents();
            // Exclude this row's current value from calculation
            const thisVal = parseFloat(amtInput.value);
            const thisValCents = !isNaN(thisVal) && thisVal > 0 ? Math.round(thisVal * 100) : 0;
            const remaining = totalCents - (allocated - thisValCents);
            if (remaining > 0) {
                amtInput.value = (remaining / 100).toFixed(2);
                updateRemaining();
            }
        });

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'btn btn-outline-danger btn-sm js-remove-line';
        removeBtn.title = 'Remove line';
        removeBtn.textContent = '×';
        removeBtn.addEventListener('click', () => {
            const rows = linesContainer.querySelectorAll('.split-line-row');
            if (rows.length <= 1) return; // can't remove last row
            row.remove();
            // Re-index remaining rows
            linesContainer.querySelectorAll('.split-line-row').forEach((r, i) => {
                (r as HTMLElement).dataset.lineIndex = String(i);
            });
            updateRemoveButtons();
            updateRemaining();
        });

        row.appendChild(amtInput);
        row.appendChild(descInput);
        row.appendChild(catContainer);
        row.appendChild(fillBtn);
        row.appendChild(removeBtn);
        return row;
    }

    // Show/hide remove buttons: hide when only one row remains
    function updateRemoveButtons(): void {
        const rows = linesContainer.querySelectorAll('.split-line-row');
        rows.forEach(row => {
            const btn = row.querySelector('.js-remove-line') as HTMLElement | null;
            if (btn) btn.style.display = rows.length <= 1 ? 'none' : '';
        });
    }

    // Populate initial lines
    if (isEdit && existingLines) {
        // NB: forEach() returns undefined — build the rows with map() before appending.
        existingLines
            .map((line, i) => createLineRow(line.amount, line.sub_cat_id, i, line.description || ''))
            .forEach(r => linesContainer.appendChild(r));
    } else {
        // New split: pre-fill with full amount + parent's current sub-category (D-04)
        linesContainer.appendChild(createLineRow(txnAmount, currentSubCatId, 0));
    }
    updateRemoveButtons();
    updateRemaining();

    // Add line button
    if (addLineBtn) {
        // Remove old listener by cloning
        const newAddBtn = addLineBtn.cloneNode(true) as HTMLButtonElement;
        addLineBtn.parentNode?.replaceChild(newAddBtn, addLineBtn);
        newAddBtn.addEventListener('click', () => {
            const count = linesContainer.querySelectorAll('.split-line-row').length;
            linesContainer.appendChild(createLineRow(null, 0, count));
            updateRemoveButtons();
            updateRemaining();
            // Focus new amount input
            const newRow = linesContainer.querySelector('.split-line-row:last-child .js-split-amount') as HTMLInputElement | null;
            newRow?.focus();
        });
    }

    // Wire save button (clone to remove stale listeners)
    const newSaveBtn = saveBtn.cloneNode(true) as HTMLButtonElement;
    saveBtn.parentNode?.replaceChild(newSaveBtn, saveBtn);
    const activeSaveBtn = modalEl.querySelector('.js-split-save') as HTMLButtonElement;

    activeSaveBtn.addEventListener('click', () => {
        if (!errorEl) return;
        errorEl.classList.add('d-none');
        errorEl.textContent = '';

        // Validate all lines
        const rows = linesContainer.querySelectorAll('.split-line-row');
        const lines: { amount: number; subCatId: number; description: string }[] = [];
        let valid = true;

        rows.forEach(row => {
            const amtInput = row.querySelector('.js-split-amount') as HTMLInputElement | null;
            const descInput = row.querySelector('.js-split-desc') as HTMLInputElement | null;
            const catSel   = row.querySelector('select') as HTMLSelectElement | null;
            const amt = parseFloat(amtInput?.value || '');
            const subCatId = parseInt(catSel?.value || '0', 10);
            const description = (descInput?.value || '').trim().slice(0, 255);

            if (!amtInput?.value || isNaN(amt)) {
                errorEl.textContent = 'Enter an amount for each line.';
                valid = false;
            } else if (amt <= 0) {
                errorEl.textContent = 'Amount must be greater than $0.00.';
                valid = false;
            } else if (!subCatId) {
                errorEl.textContent = 'Select a category for each line.';
                valid = false;
            } else {
                lines.push({ amount: amt, subCatId, description });
            }
        });

        if (!valid) {
            errorEl.classList.remove('d-none');
            return;
        }

        // Check over-allocation
        const allocated = lines.reduce((s, l) => s + Math.round(l.amount * 100), 0);
        const remaining = totalCents - allocated;
        if (remaining < 0) {
            errorEl.textContent = `Lines exceed the transaction total by $${(Math.abs(remaining) / 100).toFixed(2)}. Reduce amounts or remove a line.`;
            errorEl.classList.remove('d-none');
            return;
        }

        activeSaveBtn.disabled = true;
        activeSaveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

        const body = new URLSearchParams();
        body.set('txnId', String(txnId));
        body.set('lines', JSON.stringify(lines.map(l => ({ amount: l.amount, subCatId: l.subCatId, description: l.description }))));

        fetch(useUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                modal.hide();
                if (datatable) datatable.ajax.reload();
            } else {
                activeSaveBtn.disabled = false;
                activeSaveBtn.textContent = 'Save split';
                if (errorEl) {
                    errorEl.textContent = data.message || 'Could not save split. Please try again.';
                    errorEl.classList.remove('d-none');
                }
            }
        })
        .catch(() => {
            activeSaveBtn.disabled = false;
            activeSaveBtn.textContent = 'Save split';
            if (errorEl) {
                errorEl.textContent = 'Could not save split. Please try again.';
                errorEl.classList.remove('d-none');
            }
        });
    });

    modal.show();
}

// Initialize event bindings for the split builder modal (delegated, list page only).
function initSplitBuilderModal(): void {
    const modalEl = document.getElementById('splitTxnModal');
    if (!modalEl) return; // list page only guard

    // Delegated handler for .js-split-open (new split) — D-01/D-04
    document.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.js-split-open') as HTMLElement | null;
        if (!btn) return;
        const txnId      = parseInt(btn.dataset.txnId || '0', 10);
        const txnAmount  = parseFloat(btn.dataset.txnAmount || '0');
        const txnDesc    = btn.dataset.txnDesc || '';
        const subCatId   = parseInt(btn.dataset.subCatId || '0', 10);
        if (!txnId) return;
        splitBuilderModal(txnId, txnAmount, txnDesc, subCatId);
    });

    // Delegated handler for .js-edit-split (edit existing manual split) — D-10
    document.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.js-edit-split') as HTMLElement | null;
        if (!btn) return;
        const txnId    = parseInt(btn.dataset.txnId || '0', 10);
        const txnAmount = parseFloat(btn.dataset.txnAmount || '0');
        const txnDesc  = btn.dataset.txnDesc || '';
        const subCatId = parseInt(btn.dataset.subCatId || '0', 10);
        if (!txnId) return;
        // Existing lines are embedded in the DataTable row data
        const row = datatable?.row(btn.closest('tr') as HTMLElement);
        const rowData = row?.data?.();
        const existingLines: { amount: number; sub_cat_id: number; description?: string }[] = (rowData?.splits || []).map((s: any) => ({
            amount: parseFloat(s.amount),
            sub_cat_id: parseInt(s.sub_cat_id, 10),
            description: s.description || '',
        }));
        splitBuilderModal(txnId, txnAmount, txnDesc, subCatId, existingLines);
    });

    // Delegated handler for .js-unsplit — D-09/D-11 (reuses existing handleUnsplit signal)
    document.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.js-unsplit') as HTMLElement | null;
        if (!btn) return;
        const txnId = btn.getAttribute('data-txn-id');
        if (!txnId) return;

        // 158 UAT: confirm before unsplitting — it deletes the split line items (destructive).
        if (!confirm('Unsplit this transaction? Its split line items will be removed and it returns to a single charge.')) {
            return;
        }

        btn.setAttribute('disabled', 'disabled');
        const origText = btn.textContent || 'Unsplit';
        btn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

        const body = new URLSearchParams();
        body.set('txnId', txnId);

        fetch('?do=unsplit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                if (datatable) datatable.ajax.reload();
            } else {
                btn.removeAttribute('disabled');
                btn.textContent = origText;
                // Show error near button
                const td = btn.closest('td');
                if (td) {
                    const errSpan = document.createElement('span');
                    errSpan.className = 'text-danger small ms-1';
                    errSpan.textContent = data.message || 'Could not unsplit. Please try again.';
                    td.appendChild(errSpan);
                    setTimeout(() => errSpan.remove(), 4000);
                }
            }
        })
        .catch(() => {
            btn.removeAttribute('disabled');
            btn.textContent = origText;
        });
    });
}

function abbreviateCurrency(amount: number): string {
    if (amount >= 1_000_000) return '$' + (amount / 1_000_000).toFixed(1) + 'M';
    if (amount >= 1_000) return '$' + (amount / 1_000).toFixed(1) + 'K';
    return '$' + amount.toFixed(2);
}

function getChartById(id: string): Highcharts.Chart | undefined {
    return (Highcharts.charts as (Highcharts.Chart | undefined)[])
        .find((c: any) => c?.renderTo?.id === id);
}

let barLevel: 'main' | 'sub' = 'main';
let currentYear: string = '';   // set in stats() from txn_current_year

// Sub-color shade helper (D-08): evenly spaced brighten steps from +0.3 (lightest) to -0.3 (darkest).
// Uses (Highcharts as any).color() — TS types don't expose Highcharts.color() directly.
function subColor(parentHex: string, index: number, total: number): string {
    const maxBrighten = 0.3;
    const step = total <= 1 ? 0 : maxBrighten - (index / (total - 1)) * maxBrighten * 2;
    return (Highcharts as any).color(parentHex).brighten(step).get();
}

// Build the drilldown sub-series array for the donut (D-01/D-02).
// Groups by_sub_top10 entries by main_id; skips the 'Other' bucket (main_id=0, non-interactive D-05).
function buildDonutDrillSeries(yearData: any): any[] {
    const subs: any[] = yearData.by_sub_top10 || [];
    const byMain: Record<number, any[]> = {};
    for (const sub of subs) {
        if (sub.main_id === 0) continue;  // skip 'Other' bucket — non-interactive (D-05)
        if (!byMain[sub.main_id]) byMain[sub.main_id] = [];
        byMain[sub.main_id].push(sub);
    }
    return Object.entries(byMain).map(([mainId, mainSubs]) => {
        const total = mainSubs.length;
        return {
            id: mainId,              // matches the main-level point's drilldown: String(main_id)
            type: 'pie' as const,
            name: mainSubs[0]?.main_name ?? '',  // used in breadcrumb
            data: mainSubs.map((s, i) => ({
                name: s.sub_name,
                y: s.total,
                color: subColor(s.main_color, i, total),
            })),
        };
    });
}

function buildMonthlySeries(yearData: any): any[] {
    const byGroup = yearData.monthly_by_group || {};
    const groupTotals: [string, number][] = Object.entries(byGroup).map(
        ([g, rows]) => [g, (rows as any[]).reduce((s, r) => s + (r.total as number), 0)]
    );
    // Sort ascending by total (smallest first = drawn ON TOP under reversedStacks:true);
    // tie-break ascending group name (deterministic).
    groupTotals.sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
    const nonZero = groupTotals.filter(([, t]) => t > 0);
    return nonZero.map(([groupName]) => ({
        type: 'column' as const,
        name: groupName,
        color: groupColor(groupName),
        data: (byGroup[groupName] as any[]).map(r => r.total),
    }));
}

function getMonthCategories(yearData: any, year: string): string[] {
    const groups = Object.values(yearData.monthly_by_group || {}) as any[][];
    if (groups.length > 0) return groups[0].map((r: any) => r.month);
    // Empty-year fallback: 12 months YYYY-MM (RESEARCH Pitfall 4)
    if (year === 'All Years') return [];
    return Array.from({ length: 12 }, (_, i) =>
        year + '-' + String(i + 1).padStart(2, '0')
    );
}

// Build main-level monthly series (D-04/D-06): reads monthly_by_main, colors from by_main.main_color.
// Follows the same ascending-sort + nonZero discipline as buildMonthlySeries.
function buildMainSeries(yearData: any): any[] {
    const byMain = yearData.monthly_by_main || {};
    const mainMeta: any[] = yearData.by_main || [];
    const mainTotals: [string, number][] = Object.entries(byMain).map(
        ([name, rows]) => [name, (rows as any[]).reduce((s, r) => s + (r.total as number), 0)]
    );
    mainTotals.sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
    const nonZero = mainTotals.filter(([, t]) => t > 0);
    return nonZero.map(([mainName]) => {
        const meta = mainMeta.find((m: any) => m.main_name === mainName);
        const color = meta?.main_color ? meta.main_color : groupColor(mainName);
        return {
            type: 'column' as const,
            name: mainName,
            color,
            data: (byMain[mainName] as any[]).map(r => r.total),
        };
    });
}

// Build sub-level monthly series (D-04/D-06): reads monthly_by_sub_top10, colors via subColor().
// 'Other' bucket falls back to groupColor('Other') / '#B2BEC3' (no parent main_color).
function buildSubSeries(yearData: any): any[] {
    const bySubMonthly = yearData.monthly_by_sub_top10 || {};
    const subMeta: any[] = yearData.by_sub_top10 || [];
    const subTotals: [string, number][] = Object.entries(bySubMonthly).map(
        ([name, rows]) => [name, (rows as any[]).reduce((s, r) => s + (r.total as number), 0)]
    );
    subTotals.sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
    const nonZero = subTotals.filter(([, t]) => t > 0);
    const totalCount = nonZero.length;
    return nonZero.map(([subName], idx) => {
        const meta = subMeta.find((s: any) => s.sub_name === subName);
        let color: string;
        if (meta && meta.main_color && subName !== 'Other') {
            color = subColor(meta.main_color, idx, totalCount);
        } else {
            color = groupColor('Other') || '#B2BEC3';
        }
        return {
            type: 'column' as const,
            name: subName,
            color,
            data: (bySubMonthly[subName] as any[]).map(r => r.total),
        };
    });
}

// Level-aware bar re-render (D-04/D-06): remove all series, add new ones per barLevel, single redraw.
// Follows the remove-add-redraw discipline: remove all → add all → single redraw at the end.
function renderBarForLevel(barChart: Highcharts.Chart, yearData: any, year: string): void {
    while (barChart.series.length > 0) barChart.series[0].remove(false);
    const newSeries = barLevel === 'main' ? buildMainSeries(yearData) : buildSubSeries(yearData);
    newSeries.forEach(s => barChart.addSeries(s, false));
    // Use monthly_by_main or monthly_by_sub_top10 for x-axis categories
    const monthSource = barLevel === 'main'
        ? (Object.values(yearData.monthly_by_main || {}) as any[][])
        : (Object.values(yearData.monthly_by_sub_top10 || {}) as any[][]);
    const categories: string[] = monthSource.length > 0
        ? monthSource[0].map((r: any) => r.month)
        : getMonthCategories(yearData, year);
    barChart.xAxis[0].setCategories(categories, false);
    barChart.setTitle(
        { text: barLevel === 'main' ? 'Monthly Spending by Category' : 'Monthly Spending by Sub-Category' },
        undefined,
        false
    );
    barChart.redraw();
}


function updateStatCards(year: string, stats: any): void {
    const isAllYears = year === 'All Years';
    const isCurrentYear = year === String(new Date().getFullYear());

    const setText = (id: string, text: string) => {
        const el = document.getElementById(id);
        if (el) el.textContent = text;
    };
    const setHTML = (id: string, html: string) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = html;
    };
    const badge = (change: number | null) => {
        if (change === null || change === undefined) return '<span class="text-muted small">N/A</span>';
        const cls = change >= 0 ? 'bg-danger' : 'bg-success';
        const arrow = change >= 0 ? '&#8593;' : '&#8595;';
        return `<span class="badge ${cls}">${arrow} ${Math.abs(change)}%</span>`;
    };

    // Card 1: Total
    setText('js-stat-label-total', isAllYears ? 'All-Time Spend' : `${year} Spend`);
    setText('js-stat-value-total', '$' + number_format(stats.total ?? 0, 2));

    // Card 2: YTD / Year-vs-PrevYear
    setText('js-stat-label-ytd', isAllYears ? 'YTD Spend' : `YTD Spend`);
    if (isAllYears) {
        setText('js-stat-value-ytd', '$' + number_format(stats.ytd ?? 0, 2));
        setHTML('js-stat-badge-ytd', badge(stats.ytd_change ?? null));
    } else {
        setText('js-stat-value-ytd', '$' + number_format(stats.total ?? 0, 2));
        setHTML('js-stat-badge-ytd', badge(stats.change ?? null));
    }

    // Card 3: MTD / Best Month
    // Phase 158 folded-todo fix: per-year payloads lack mtd/mtd_change keys (they are only
    // in the 'All Years' payload from getStats()). When the current year is selected,
    // fall back to txn_payload['All Years'].stats.mtd — correct because all-time MTD = current-year MTD by definition.
    if (isAllYears || isCurrentYear) {
        setText('js-stat-label-mtd', 'MTD Spend');
        const allYearsStats = (typeof txn_payload !== 'undefined' && txn_payload['All Years']?.stats) ?? {};
        const mtdValue     = isAllYears ? (stats.mtd ?? 0) : (allYearsStats.mtd ?? 0);
        const mtdChange    = isAllYears ? (stats.mtd_change ?? null) : (allYearsStats.mtd_change ?? null);
        setText('js-stat-value-mtd', '$' + number_format(mtdValue, 2));
        setHTML('js-stat-badge-mtd', badge(mtdChange));
    } else {
        setText('js-stat-label-mtd', `Best Month ${year}`);
        if (stats.best_month) {
            setText('js-stat-value-mtd',
                `${stats.best_month} — $${number_format(stats.best_month_total ?? 0, 2)}`);
        } else {
            setText('js-stat-value-mtd', '$0.00');
        }
        setHTML('js-stat-badge-mtd', '');
    }

    // Card 4: Count
    setText('js-stat-label-count', 'Transactions');
    setText('js-stat-value-count', String(stats.count ?? 0));

    // Card 5: Avg
    setText('js-stat-label-avg', 'Avg / Transaction');
    setText('js-stat-value-avg', '$' + number_format(stats.avg ?? 0, 2));
}

function onYearChange(year: string): void {
    const yearData = txn_payload[year];
    if (!yearData) return;
    currentYear = year;

    // --- Bar chart: level-aware re-render (D-06 — toggle composes with year-select) ---
    const barChart = getChartById('chart_txn_monthly');
    if (barChart) {
        renderBarForLevel(barChart, yearData, year);
    }

    // --- Donut chart: D-03 reset — drill up if drilled, replace data + drilldown.series, setSubtitle ---
    const donutChart = getChartById('chart_txn_donut');
    if (donutChart) {
        // Drill up until at main level (drilldownLevels is internal Highcharts state)
        while ((donutChart as any).drilldownLevels?.length > 0) {
            donutChart.drillUp();
        }
        const byMain = yearData.by_main || [];
        const total = byMain.reduce((s: number, d: any) => s + d.total, 0);
        if (donutChart.series[0]) {
            donutChart.series[0].setData(
                byMain.map((d: any) => {
                    const p: any = { name: d.main_name, y: d.total, ...(d.main_id !== 0 ? { drilldown: String(d.main_id) } : {}) };
                    if (d.main_color) p.color = d.main_color;  // conditional spread — never explicit undefined
                    return p;
                }),
                false  // redraw=false — batch with update() below, single redraw at end
            );
        }
        // Replace stale drilldown.series from previous year (Pitfall 3 — chart.update() is the documented fix)
        (donutChart as any).update({ drilldown: { series: buildDonutDrillSeries(yearData) } }, false);
        donutChart.setSubtitle({ text: abbreviateCurrency(total) });
        donutChart.redraw();
    }

    // --- Stat cards ---
    updateStatCards(year, yearData.stats);
}

function stats() {
    if (typeof txn_donut === 'undefined') return;

    // Phase 181: theme ternary blocks removed; getChartTheme() supplies all chrome via merge.
    // Data-series identity colors (main_color, groupColor) remain FIXED (D-01).

    currentYear = String(txn_current_year);

    const initialYearData = txn_payload[currentYear] ?? txn_payload['All Years'];
    const initialByMain = initialYearData.by_main || [];
    const initialDonutTotal = initialByMain.reduce((s: number, d: any) => s + d.total, 0);

    // Donut chart — spending by main category with native drilldown into subs (D-01/D-02)
    Highcharts.chart('chart_txn_donut', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'pie',
            height: 350,
            events: {
                // D-03: drilling updates center subtitle to drilled main's total
                drilldown: function(this: Highcharts.Chart, e: any) {
                    const subTotal = (e.seriesOptions.data as any[]).reduce((s: number, p: any) => s + p.y, 0);
                    this.setSubtitle({ text: abbreviateCurrency(subTotal) });
                },
                // D-03: drillup resets center subtitle to main-level grand total
                drillup: function(this: Highcharts.Chart) {
                    const yearData = txn_payload[currentYear] ?? txn_payload['All Years'];
                    const mainTotal = (yearData.by_main || []).reduce((s: number, d: any) => s + d.total, 0);
                    this.setSubtitle({ text: abbreviateCurrency(mainTotal) });
                }
            }
        },
        title: { text: 'Spending by Category', style: { fontSize: '14px' } },
        subtitle: {
            text: abbreviateCurrency(initialDonutTotal),
            floating: true,
            verticalAlign: 'middle',
            y: 10,
            style: { fontSize: '16px', fontWeight: 'bold' }
        },
        legend: {
            enabled: true,
            layout: 'vertical' as const,
            align: 'right' as const,
            verticalAlign: 'middle' as const,
        },
        plotOptions: {
            pie: {
                innerSize: '60%',
                showInLegend: true,
                dataLabels: { enabled: false },
            }
        },
        series: [{
            type: 'pie',
            name: 'Amount',
            data: initialByMain.map((d: any) => {
                const p: any = { name: d.main_name, y: d.total, ...(d.main_id !== 0 ? { drilldown: String(d.main_id) } : {}) };
                if (d.main_color) p.color = d.main_color;  // conditional spread — never explicit undefined (project memory)
                return p;
            })
        }],
        drilldown: {
            series: buildDonutDrillSeries(initialYearData),
            // Phase 181: breadcrumb text color comes from getChartTheme() xAxis.title.style.color
            // (textPrimary token) — no hardcoded foreground needed.
            breadcrumbs: {
                position: { align: 'left' },
                buttonTheme: {
                    style: {},
                    states: { hover: { style: {} }, select: { style: {} } }
                },
                separator: { style: {} }
            }
        },
        credits: { enabled: false }
    } as any));

    // Stacked column chart — monthly spending by category (defaults to Main level, D-04)
    const yearData = txn_payload[currentYear] ?? txn_payload['All Years'];
    const initialBarSeries = buildMainSeries(yearData);  // default Main level
    const initialBarCats = (Object.values(yearData.monthly_by_main || {}) as any[][])[0]?.map((r: any) => r.month)
        ?? getMonthCategories(yearData, currentYear);

    Highcharts.chart('chart_txn_monthly', Highcharts.merge(getChartTheme(), {
        chart: { type: 'column', height: 380 },
        title: { text: 'Monthly Spending by Category', style: { fontSize: '14px' } },
        xAxis: {
            type: 'category',
            categories: initialBarCats,
            labels: { rotation: -45 },
        },
        yAxis: {
            title: { text: 'Amount ($)' },
            gridLineWidth: 0.1,
        },
        tooltip: {
            shared: true,
            // Categorical x-axis: this.x is the month label string (e.g. "Jan 2026"),
            // NOT a timestamp — override the helper's default date header so it
            // doesn't run Highcharts.dateFormat on a string (RESEARCH Q3).
            formatter: makeEmptyAwareTooltip({
                shared: true,
                formatHeader: (ctx) => `<b>${ctx.x}</b>`,
            }),
        },
        legend: {
            enabled: true,
            layout: 'horizontal' as const,
            align: 'center' as const,
            verticalAlign: 'bottom' as const,
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                borderWidth: 0,
                states: { hover: { borderWidth: 1 } },
            },
            // NO point.events.click — DASH-03 out of scope (success criterion #5).
        },
        series: initialBarSeries,
        credits: { enabled: false },
    } as any));

    // Wire year-select change listener
    const yearSelect = document.getElementById('js-year-select') as HTMLSelectElement | null;
    if (yearSelect) {
        yearSelect.addEventListener('change', () => {
            onYearChange(yearSelect.value);
        });
    }

    // Wire Main|Sub toggle (D-04/D-06): re-render bar at selected level for current year
    const barToggle = document.getElementById('js-bar-level-toggle');
    if (barToggle) {
        barToggle.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest('button[data-level]') as HTMLButtonElement | null;
            if (!btn) return;
            const level = btn.getAttribute('data-level') as 'main' | 'sub' | null;
            if (!level || level === barLevel) return;
            barLevel = level;
            // Move .active class to clicked button
            barToggle.querySelectorAll('button[data-level]').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            // Re-render bar for current year at new level
            const bar = getChartById('chart_txn_monthly');
            if (bar) {
                const yd = txn_payload[currentYear] ?? txn_payload['All Years'];
                renderBarForLevel(bar, yd, currentYear);
            }
        });
    }

    // On initial page load: if currentYear is NOT 'All Years' AND the server-rendered
    // stat cards came from getStats() (which is all-time), we must overwrite them
    // with the current-year payload so the page matches the selected year on first paint.
    if (currentYear !== 'All Years' && txn_payload[currentYear]) {
        updateStatCards(currentYear, txn_payload[currentYear].stats);
    }
}

let datatable : any;

// Phase 158 D-14: per-session flow toggle state (reset on page reload; no localStorage/cookie).
// Default = debit-only (spending view). Toggle shows all transactions (debit + credit + income).
let showAll = false;

// Phase 260 (XFER-03/D-13): per-session transfers-reveal toggle state, same shape as showAll.
// Default = hidden (is_internal_transfer=1 rows excluded); toggle reveals them.
let showTransfers = false;

// Phase 260 (XFER-03) — POST a URL-encoded body to a signal URL, parse the JSON response.
// Shared by the ledger's mark/unmark handlers and initTransfersPage()'s rule-CRUD handlers.
// WR-11 option (a): X-Requested-With is REQUIRED by TransactionsPresenter::requireAjaxPost() on
// all five Phase 260 signals. It is not a CORS-safelisted header, so setting it forces a preflight
// that a cross-origin attacker cannot satisfy — that, not the session cookie's SameSite default,
// is what makes these write signals CSRF-safe. Removing this header silently 403s every transfer
// action. Matches the convention already used by ask.ts, shared.ts, util.ts, stardew.ts and mc.ts.
function postTransferJson(url: string, params: Record<string, string>): Promise<any> {
    const body = new URLSearchParams(params);
    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
    }).then(r => r.json());
}

/**
 * Render the child-row item breakdown for a split-parent transaction.
 *
 * Called from the DataTables row.child() API with the splits array from the row data.
 * All data is embedded in the row JSON — NO AJAX per-row (N+1 prevention, RD-06).
 *
 * GATED zero-amount display: when item.amount == 0, render "— CAD" and add a note
 * row ("Item amounts pending official Amazon export.") — never shows $0.00 (RD-04).
 *
 * Phase 158 D-10/D-11: for manual splits (isAmazonSplit=false), footer note changes to
 * "To edit the split, use the Edit button." and per-item recategorize link is removed.
 * For Amazon splits, existing read-only recategorize pattern is preserved.
 */
function renderSplitChildren(splits: any[], isAmazonSplit: boolean = true): string {
    if (!splits || splits.length === 0) {
        return '<div class="p-2 ps-4"><p class="text-muted small mb-0">No item breakdown available.</p></div>';
    }

    const allZero = splits.every((s: any) => parseFloat(s.amount) === 0);

    let rows = splits.map((item: any) => {
        const amt = parseFloat(item.amount);
        const amtCell = (amt === 0)
            ? '<td class="text-end small text-muted">— CAD</td>'
            : `<td class="text-end small text-danger">$${number_format(amt, 2)} CAD</td>`;
        const catName = escapeHtml(String(item.sub_cat_name || 'Uncategorized'));
        // Manual splits may have no description (nothing was ever entered) — show a muted
        // dash rather than an empty Item cell, which reads as a rendering fault.
        const rawDesc = String(item.description ?? '').trim();
        const descAttr = escapeHtml(rawDesc);
        const descCell = rawDesc === '' ? '<span class="text-muted">&mdash;</span>' : descAttr;
        // For Amazon splits: show recategorize button (D-11). For manual splits: read-only category badge.
        const catCell = isAmazonSplit
            ? `<td><button type="button" class="btn btn-link btn-sm p-0 text-start text-decoration-none js-split-cat-edit" data-split-id="${item.id}" data-sub-cat-id="${item.sub_cat_id ?? ''}" title="Change category"><span class="badge border text-body-secondary">${catName}</span> <span class="text-muted small">&#9998;</span></button></td>`
            : `<td><span class="badge border text-body-secondary">${catName}</span></td>`;
        return `<tr>
            <td class="small" style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${descAttr}">${descCell}</td>
            ${catCell}
            ${amtCell}
        </tr>`;
    }).join('');

    // Add gated pending-amounts note row when all amounts are zero
    if (allZero) {
        rows += `<tr><td colspan="3" class="text-muted small fst-italic">Item amounts pending official Amazon export.</td></tr>`;
    }

    // Footer total (hidden when all amounts are zero)
    let tfoot = '';
    if (!allZero) {
        const total = splits.reduce((s: number, item: any) => s + parseFloat(item.amount || '0'), 0);
        tfoot = `<tfoot><tr class="fw-semibold">
            <td colspan="2" class="small fw-semibold text-end">Total</td>
            <td class="text-end small fw-semibold text-danger">$${number_format(total, 2)} CAD</td>
        </tr></tfoot>`;
    }

    const reviewUrl = document.querySelector('[data-review-url]')?.getAttribute('data-review-url') ?? '/transactions/review';

    // D-10/D-11: footer note differs by split type
    const footerNote = isAmazonSplit
        ? `Read-only. To recategorize an item, use the <a href="${reviewUrl}">Review Queue</a>.`
        : `To edit the split, use the Edit button.`;

    return `<div class="p-2 ps-4">
        <p class="small fw-semibold mb-2 text-muted">Order Items</p>
        <div class="table-responsive">
            <table class="table table-sm table-bordered mb-0">
                <thead>
                    <tr><th>Item</th><th>Category</th><th>Amount</th></tr>
                </thead>
                <tbody>${rows}</tbody>
                ${tfoot}
            </table>
        </div>
        <p class="text-muted small mt-1 mb-0">${footerNote}</p>
    </div>`;
}

function list() {
    let table = document.querySelector('.datatable[data-table="transactions_list"]') as HTMLElement;
    if(!table) {
        return;
    }

    // 158 UAT: expand/collapse chevrons (used by the row action cluster + the expand handler).
    const CHEVRON_RIGHT = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/></svg>';
    const CHEVRON_DOWN  = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/></svg>';

    datatable = new DataTable('.datatable[data-table="transactions_list"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getTransactions',
            data: function(data, settings) {
                let search = settings.oPreviousSearch.search;

                let search_attr = table.getAttribute('data-search') as string;
                if(search_attr && search === '') {
                    data.search.value = search_attr;
                    search = search_attr;
                }

                // 158 UAT: fetch BOTH the spending (debit) and credit/income totals in one
                // request and update both header spans (kept gross/separate — not netted).
                let total = document.querySelector('.js-transactions-total') as HTMLElement;
                let totalCredit = document.querySelector('.js-transactions-total-credit') as HTMLElement;
                if(total) {
                    get('?do=getTotals', {search: search}, (data: any) => {
                        if(data) {
                            total.innerText = number_format(data.debit ?? 0);
                            if(totalCredit) totalCredit.innerText = number_format(data.creditIncome ?? 0);
                        }
                    });
                }
                // Phase 158 D-14: send flow toggle state so server applies/removes debit-only filter
                // data.showAll consumed by server-side DataTable handler (Plan 03 Task 2)
                data.showAll = showAll ? '1' : '0';
                // Phase 260 (XFER-03/D-13): send transfers-reveal toggle state so the server
                // includes/excludes is_internal_transfer=1 rows (getTransactionsTable()'s
                // trailing $showTransfers parameter, same bound-$where-map shape as showAll).
                data.showTransfers = showTransfers ? '1' : '0';
                return data;
            }
        },
        columns: [
            { data: 'type' },
            { data: 'account', render: function(acct: any) {
                // 158 UAT: mask account to the last 4 digits with asterisks; Simplii sends "0"
                // (or blank) for accounts it doesn't expose — show an em dash instead of "0".
                const s = (acct ?? '').toString().trim();
                if (s === '' || /^0+$/.test(s)) return '<span class="text-muted">—</span>';
                return '<span class="font-monospace">****' + s.slice(-4) + '</span>';
            } },
            { data: 'date' },
            { data: null, render: function(data) {
                // Append "Split" badge on the Name column for split-parent rows
                const name = data.name ?? '';
                const key  = data.merchant_key ?? '';
                // D-02: link to merchant deep-dive page using server-provided normalized key (MERCH-01)
                // encodeURIComponent handles spaces/special chars in normalized keys
                const nameHtml = key
                    ? `<a href="/transactions/merchant?name=${encodeURIComponent(key)}">${name}</a>`
                    : name;
                // 195-05 (BANK-10/D-11): inline merchant logo, prepended before the name/link.
                // Hot-linked Plaid CDN URL — plain <img src>, no CORS/local storage needed.
                // encodeURI (NOT encodeURIComponent — logo_url is a full URL, not one component)
                // strips quote/angle-bracket characters from the interpolated src attribute
                // (T-195-05-X XSS mitigation); onerror hides the <img> on a broken/missing logo
                // (D-11 "nothing" fallback — mirrors mc.ts's avatar onerror idiom).
                let logoHtml = '';
                if (data.logo_url) {
                    logoHtml = `<img src="${encodeURI(data.logo_url)}" loading="lazy" width="16" height="16" `
                        + `alt="" class="rounded me-1" style="vertical-align:-3px" onerror="this.style.display='none'">`;
                }
                let badges = '';
                // 195-05 (BANK-10): ⏳ pending badge — purely data-driven on plaid_pending, so it
                // auto-clears the instant BankSyncModel's pending->posted reconciliation (D-03)
                // flips the flag to 0 on the row's next poll/redraw — no separate "clear" action.
                if (data.plaid_pending === 1) {
                    badges += ' <span class="badge bg-warning bg-opacity-10 text-warning ms-1" title="Pending — Plaid has not posted this transaction yet; the badge clears automatically once it settles">⏳ Pending</span>';
                }
                // 158 UAT: dropped the "Split" badge — split rows are already signalled by the
                // expand (+/-) control and the Edit/Unsplit action buttons, so it was redundant.
                // D-05 audit marker: auto badge for e-transfer memory auto-applied transactions
                if (data.auto_applied_memory === 1) {
                    badges += ' <span class="badge bg-secondary bg-opacity-10 text-secondary ms-1" title="Category set automatically by e-transfer recipient memory">auto</span>';
                }
                // 158 UAT — flow indicator (matches merchant-page D-08 badge styling). Credit/refund
                // and income rows are flagged so they're distinguishable when "Show all" is on; debit
                // rows stay clean since the list is a spending (debit) view by default.
                if (data.flow === 'credit') {
                    badges += ' <span class="badge bg-success bg-opacity-10 text-success ms-1" title="Credit / refund — not counted in spending">Credit</span>';
                } else if (data.flow === 'income') {
                    badges += ' <span class="badge bg-info bg-opacity-10 text-info ms-1" title="Income — not counted in spending">Income</span>';
                }
                return logoHtml + nameHtml + badges;
            }},
            // Phase 196 (TRACK-08/D-11) — "view tracking" affordance placed right after Name:
            // parcel/truck icon color-coded by the linked parcel's most-actionable status bucket;
            // opens the parcel(s). tracking_status/tracking_ids attached server-side by
            // TransactionsModel::getTransactionsTable() (guarded LEFT JOIN, empty when unlinked).
            // Icon sized to 1em (matches row font height); color via Bootstrap text-* utility.
            { data: null, orderable: false, render: function(data) {
                const ids: number[] = data.tracking_ids || [];
                if (!ids.length) return '';
                const status = data.tracking_status || '';
                const colorClass = (status === 'Exception' || status === 'DeliveryFailure') ? 'text-danger'
                    : (status === 'Delivered') ? 'text-success'
                    : 'text-warning'; // OutForDelivery / InTransit / InfoReceived bucket
                const title = ids.length > 1 ? (ids.length + ' linked parcels') : 'View tracking';
                return '<a href="/tracking/' + ids[0] + '" class="' + colorClass + '" title="' + title + '">'
                     + '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" '
                     + 'style="height:1em;width:0.875em;vertical-align:-0.125em" fill="currentColor" aria-hidden="true"><path d="M50.7 58.5L0 160H208V32H93.7c-18.2 0-34.8 10.3-43 26.5zM240 160h208L397.3 58.5C389.1 42.3 372.5 32 354.3 32H240V160zm208 32H0l0 208c0 26.5 21.5 48 48 48l352 0c26.5 0 48-21.5 48-48l0-208z"/></svg>'
                     + '</a>';
            }},
            //{ data: 'description' },
            { data: null, orderable: false, render: function(data) {
                // 158 UAT — Category column. Resolve sub_cat_id → sub-category name via the
                // injected categoryTree. Split parents show "(split)" (their children carry the
                // categories); other rows are click-to-recategorize (.js-txn-cat-edit → #txnCatModal).
                if (data.is_split_parent === 1) {
                    // 158 UAT: the "(split)" label IS the expand/collapse toggle for the line items
                    // (the separate chevron button was removed to save space). Reuses .js-split-expand.
                    return '<a href="#" role="button" aria-expanded="false" '
                         + 'class="js-split-expand text-decoration-none text-muted small" '
                         + 'title="Show / hide line items">' + CHEVRON_RIGHT + ' (split)</a>';
                }
                // Phase 260 (WR-04): a flagged transfer is not spend, so there is no category to
                // pick (Discretion Resolution 5). The "Show transfers" reveal only rewrote the
                // AMOUNT cell's action cluster, so these rows still rendered a .js-txn-cat-edit
                // link and the POST behind it still succeeded — one click undid the invariant
                // insert, mark, reapply and backfill all enforce. setTransactionCategory() now
                // refuses server-side; this suppresses the affordance so it is never offered.
                if (data.is_internal_transfer === 1) {
                    return '<span class="text-muted" title="Excluded as an internal transfer — not spend, so there is no category">—</span>';
                }
                const tree: any[] = (window as any).categoryTree || [];
                const subId = data.sub_cat_id != null ? parseInt(data.sub_cat_id, 10) : 0;
                let name = '';
                for (const main of tree) {
                    const hit = (main.subs || []).find((s: any) => s.id === subId);
                    if (hit) { name = hit.name; break; }
                }
                const label = name
                    ? '<span class="badge bg-secondary bg-opacity-10 text-body">' + name + '</span>'
                    : '<span class="text-muted">Uncategorized</span>';
                return '<a href="#" class="js-txn-cat-edit text-decoration-none" data-txn-id="'
                     + (data.id ?? '') + '" data-sub-cat-id="' + subId
                     + '" title="Click to change category">' + label + '</a>';
            }},
            { data: null, className: 'js-txn-amount-cell', render: function(data) {
                return '$'+number_format(data.amount,2);
            }},
        ],
        order: [[2,"desc"]] as Order[],
        // Phase 183 (UX-02, D-09/D-10): empty-state for 0-row result sets.
        // DataTables server-side AJAX delivers the 0-row guard for this table
        // (there is no Latte foreach — rows arrive via AJAX). The language.emptyTable
        // HTML mirrors the .ksat-empty-state component style (T-183-09: guards $row->undeclared).
        language: {
            emptyTable: '<div class="ksat-empty-state text-center py-5">' +
                '<div class="ksat-empty-state__icon mb-3" aria-hidden="true">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" style="opacity:.35;color:var(--bs-secondary-color)" viewBox="0 0 16 16" role="img" aria-hidden="true">' +
                '<path d="M4.98 4a.5.5 0 0 0-.39.188L1.54 8H6a.5.5 0 0 1 .5.5 1.5 1.5 0 1 0 3 0A.5.5 0 0 1 10 8h4.46l-3.05-3.812A.5.5 0 0 0 11.02 4H4.98zm9.954 5H10.45a2.5 2.5 0 0 1-4.9 0H1.066l.32 2.562A.5.5 0 0 0 1.88 12h12.24a.5.5 0 0 0 .496-.438L14.933 9zM3.809 3.563A1.5 1.5 0 0 1 4.981 3H11.02a1.5 1.5 0 0 1 1.172.563l3.7 4.625a.5.5 0 0 1 .105.374l-.39 3.124A1.5 1.5 0 0 1 14.117 13H1.883a1.5 1.5 0 0 1-1.489-1.314L.004 8.562a.5.5 0 0 1 .105-.374l3.7-4.625z"/>' +
                '</svg></div>' +
                '<p class="text-muted mb-3">No transactions found. Try adjusting your date range or import a CSV.</p>' +
                '<a href="/transactions/import" class="btn btn-outline-secondary btn-sm">Import CSV</a>' +
                '</div>',
        },
        // Phase 183 (UX-02, D-08/D-10): skeleton + empty-state via drawCallback (v2 DataTables).
        // MEMORY: DataTables redraw → use drawCallback (v2, native draw listeners dead).
        // After each AJAX draw: hide skeleton; show/hide empty-state based on row count.
        drawCallback: function() {
            // Hide skeleton once data (or empty-state) renders after each AJAX draw
            const skel = document.querySelector('.ksat-transactions-skeleton') as HTMLElement | null;
            if (skel) skel.hidden = true;
            // Show the Latte-rendered empty-state when 0 rows are returned (T-183-09).
            // Phase 183 hotfix: inside a DataTables drawCallback `this` is the settings object,
            // NOT the API — so `this.page` was undefined and threw, which stalled DataTables'
            // own processing indicator ("stuck on loading"). Use the outer `datatable` API
            // instance (assigned synchronously before the async draw fires) instead.
            const empty = document.querySelector('.ksat-transactions-empty') as HTMLElement | null;
            if (empty && datatable) {
                const rowCount = datatable.page.info().recordsDisplay;
                empty.hidden = rowCount > 0;
            }
        },
        rowCallback: function(row: HTMLElement, data: any) {
            // 158 UAT: the expand/collapse control used to be PREPENDED to the type column,
            // which looked odd — only split rows had it, so the column misaligned. It now lives
            // in the action cluster (chevron added below), keeping the type column uniform.

            // Phase 158 D-08/D-09/D-11 — Row action buttons in the last cell.
            // eligibility gate: flow IN ('debit','credit') AND not already a split parent → "Split"
            // Manual split parent (is_split_parent=1, is_amazon_split=false) → "Edit" + "Unsplit"
            // Amazon split parent (is_split_parent=1, is_amazon_split=true) → "Unsplit" only
            // income / no-split rows → no action button
            // Phase 196 fix: the action cluster belongs on the AMOUNT line, not literally the
            // last cell — Wave 4 added a Tracking column AFTER Amount, so `td:last-child` now
            // points at the tracking cell and would clobber the "view tracking" truck. Target the
            // amount cell explicitly (js-txn-amount-cell) so the tracking column is left intact.
            const lastTd = row.querySelector('td.js-txn-amount-cell') as HTMLTableCellElement | null;
            if (!lastTd) return;

            const flow = data.flow ?? '';
            const isSplitParent = data.is_split_parent === 1;
            const isAmazonSplit = !!data.is_amazon_split;
            const txnId = data.id ?? '';
            const txnAmount = parseFloat(data.amount ?? '0');
            const txnDesc = (data.name ?? '') + ' — $' + number_format(txnAmount, 2) + ' CAD';
            const subCatId = parseInt(data.sub_cat_id ?? '0', 10);
            // Phase 260 (XFER-03/D-13): is_internal_transfer/transfer_source are attached per row
            // by getTransactionsTable() (Plan 07) — only visible on the DataTable payload when
            // the reveal toggle is on (server hides is_internal_transfer=1 rows by default).
            const isTransfer = data.is_internal_transfer === 1;

            // 158 UAT: keep the action buttons on the SAME line as the amount and make them
            // extra-small. Bootstrap btn CSS-vars shrink padding/font below btn-sm.
            const SMALL_BTN = '--bs-btn-padding-y:.05rem;--bs-btn-padding-x:.45rem;--bs-btn-font-size:.72rem;line-height:1.35;';
            const actionWrapper = document.createElement('div');
            actionWrapper.className = 'd-inline-flex gap-1 flex-nowrap';

            // 158 UAT: the expand/collapse toggle for split line items now lives on the "(split)"
            // label in the Category column (saves action-cluster space) — see the category render.

            if (isTransfer) {
                // Phase 260 (XFER-03/D-12/D-13): a flagged row (only ever visible with the
                // "Show transfers" toggle on) shows its provenance badge + Unmark, never
                // Split/Edit/Unsplit — Discretion Resolution 6: transfer rows are not splittable.
                const badge = document.createElement('span');
                badge.className = 'badge bg-secondary bg-opacity-10 text-secondary';
                const source = data.transfer_source ?? '';
                badge.textContent = source === 'auto_rule' ? 'Rule'
                    : source === 'plaid_pfc' ? 'Plaid'
                    : 'Manual';
                badge.title = 'Excluded as an internal transfer';
                actionWrapper.appendChild(badge);

                const unmarkBtn = document.createElement('button');
                unmarkBtn.type = 'button';
                unmarkBtn.className = 'btn btn-outline-secondary js-transfer-action';
                unmarkBtn.style.cssText = SMALL_BTN;
                unmarkBtn.dataset.txnId = String(txnId);
                unmarkBtn.dataset.action = 'unmark';
                unmarkBtn.textContent = 'Unmark';
                actionWrapper.appendChild(unmarkBtn);
            } else if (!isSplitParent && (flow === 'debit' || flow === 'credit')) {
                // Eligible unsplit row → "Split" button (D-09)
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'btn btn-outline-secondary js-split-open';
                btn.style.cssText = SMALL_BTN;
                btn.dataset.txnId = String(txnId);
                btn.dataset.txnAmount = String(txnAmount);
                btn.dataset.txnDesc = txnDesc;
                btn.dataset.subCatId = String(subCatId);
                btn.textContent = 'Split';
                actionWrapper.appendChild(btn);

                // Phase 260 (XFER-03/D-04): mark-as-transfer is debit-only, mirroring
                // markAsTransfer()'s own server-side guard (a credit matching an issuer token is
                // a statement credit/reversal, not a bill payment — D-04).
                if (flow === 'debit') {
                    const markBtn = document.createElement('button');
                    markBtn.type = 'button';
                    markBtn.className = 'btn btn-outline-secondary js-transfer-action';
                    markBtn.style.cssText = SMALL_BTN;
                    markBtn.dataset.txnId = String(txnId);
                    markBtn.dataset.action = 'mark';
                    markBtn.textContent = 'Mark transfer';
                    actionWrapper.appendChild(markBtn);
                }
            } else if (isSplitParent && !isAmazonSplit) {
                // Manually-split parent → "Edit" + "Unsplit" (D-09/D-10)
                const editBtn = document.createElement('button');
                editBtn.type = 'button';
                editBtn.className = 'btn btn-outline-secondary js-edit-split';
                editBtn.style.cssText = SMALL_BTN;
                editBtn.dataset.txnId = String(txnId);
                editBtn.dataset.txnAmount = String(txnAmount);
                editBtn.dataset.txnDesc = txnDesc;
                editBtn.dataset.subCatId = String(subCatId);
                editBtn.textContent = 'Edit';
                actionWrapper.appendChild(editBtn);

                const unsplitBtn = document.createElement('button');
                unsplitBtn.type = 'button';
                unsplitBtn.className = 'btn btn-outline-danger js-unsplit';
                unsplitBtn.style.cssText = SMALL_BTN;
                unsplitBtn.dataset.txnId = String(txnId);
                unsplitBtn.textContent = 'Unsplit';
                actionWrapper.appendChild(unsplitBtn);
            } else if (isSplitParent && isAmazonSplit) {
                // Amazon-split parent → "Unsplit" only (D-11)
                const unsplitBtn = document.createElement('button');
                unsplitBtn.type = 'button';
                unsplitBtn.className = 'btn btn-outline-danger js-unsplit';
                unsplitBtn.style.cssText = SMALL_BTN;
                unsplitBtn.dataset.txnId = String(txnId);
                unsplitBtn.textContent = 'Unsplit';
                actionWrapper.appendChild(unsplitBtn);
            }
            // income rows (flow='income') → no action button (D-08)

            if (actionWrapper.children.length > 0) {
                // Put amount + buttons on ONE line: rewrap the amount text into a flex row
                // alongside the buttons (158 UAT — buttons were dropping below the amount).
                const amountText = (lastTd.textContent || '').trim();
                lastTd.textContent = '';
                const cellFlex = document.createElement('div');
                cellFlex.className = 'd-flex align-items-center gap-2 flex-nowrap';
                const amtSpan = document.createElement('span');
                amtSpan.className = 'text-nowrap';
                amtSpan.textContent = amountText;
                cellFlex.appendChild(amtSpan);
                cellFlex.appendChild(actionWrapper);
                lastTd.appendChild(cellFlex);
            }
        }
    });

    // Phase 183 (UX-02, D-08/D-10): Show skeleton shimmer immediately on page load
    // while the DataTable's first AJAX fetch is in-flight. drawCallback hides it.
    const txnSkel = document.querySelector('.ksat-transactions-skeleton') as HTMLElement | null;
    if (txnSkel) txnSkel.hidden = false;

    // ── Child-row expand handler (split parents only, gated to list page) ─────
    // Delegated click on the DataTable wrapper so it works after ajax.reload().
    // data-table="transactions_list" check guards against double-bind on other pages
    // (Pitfall 5 — index.ts init()s every module on every page; T-152-11).
    const tableWrapper = document.querySelector('.datatable[data-table="transactions_list"]');
    if (!tableWrapper) return;

    tableWrapper.addEventListener('click', function(e: Event) {
        const btn = (e.target as HTMLElement).closest('.js-split-expand') as HTMLElement | null;
        if (!btn) return;
        e.preventDefault(); // the toggle is now an <a> ("(split)") in the Category column

        const tr = btn.closest('tr') as HTMLTableRowElement | null;
        if (!tr) return;

        const row = datatable.row(tr);
        const rowData: any = row.data();
        if (!rowData || rowData.is_split_parent !== 1) return;

        // D-10/D-11: pass is_amazon_split flag to renderSplitChildren
        const isAmazonSplit = !!rowData.is_amazon_split;

        if (row.child.isShown()) {
            row.child.hide();
            btn.setAttribute('aria-expanded', 'false');
            btn.innerHTML = CHEVRON_RIGHT + ' (split)';
        } else {
            row.child(renderSplitChildren(rowData.splits || [], isAmazonSplit)).show();
            btn.setAttribute('aria-expanded', 'true');
            btn.innerHTML = CHEVRON_DOWN + ' (split)';
        }
    });

    // ── Phase 158 D-14: Flow toggle handler (per-session showAll state) ──────
    // Flips showAll, updates button label, reloads DataTable with new showAll param.
    // Composes with existing Phase 154 mainCatId/subCatId filters (independent params).
    const flowToggleBtn = document.querySelector('.js-flow-toggle') as HTMLButtonElement | null;
    if (flowToggleBtn) {
        flowToggleBtn.addEventListener('click', () => {
            showAll = !showAll;
            flowToggleBtn.textContent = showAll ? 'Show all transactions' : 'Spending only';
            flowToggleBtn.dataset.showAll = showAll ? '1' : '0';
            if (datatable) datatable.ajax.reload();
        });
    }

    // ── Phase 260 (XFER-03/D-13): Transfers-reveal toggle handler ─────────────
    // Same shape as the flow toggle above: flips showTransfers, updates the button label,
    // reloads the DataTable with the new showTransfers param.
    const transfersToggleBtn = document.querySelector('.js-transfers-toggle') as HTMLButtonElement | null;
    if (transfersToggleBtn) {
        transfersToggleBtn.addEventListener('click', () => {
            showTransfers = !showTransfers;
            transfersToggleBtn.textContent = showTransfers ? 'Hide transfers' : 'Show transfers';
            transfersToggleBtn.dataset.showTransfers = showTransfers ? '1' : '0';
            if (datatable) datatable.ajax.reload();
        });
    }

    // ── Phase 260 (XFER-03/D-10/D-11) — ledger mark/unmark, delegated + idempotent ─────
    // Signal URLs come from the js-transfer-urls hidden element (list.latte Task 2) — never
    // hardcoded ?do=... strings (T-260-36). Single delegated listener on the table wrapper,
    // guarded by an idempotency flag matching initQuarantinePanel()'s shape (T-260-38).
    //
    // WR-09: there are no string fallbacks. The old `?? '?do=markTransfer'` defaults silently
    // masked a missing .js-transfer-urls element, and a relative '?do=' replaces the page's
    // ENTIRE query string — it resolved only because /transactions happens to carry none
    // today. Since one of these three URLs drives a COMMIT (confirm:'1'), a mis-targeted
    // request there is a bulk write, not a preview. A missing element or attribute now fails
    // loudly and binds nothing.
    const transferUrls = document.querySelector('.js-transfer-urls') as HTMLElement | null;
    const markUrl    = transferUrls?.dataset.markUrl ?? '';
    const unmarkUrl  = transferUrls?.dataset.unmarkUrl ?? '';
    const reapplyUrl = transferUrls?.dataset.reapplyUrl ?? '';

    if (!markUrl || !unmarkUrl || !reapplyUrl) {
        console.error(
            '[transactions] .js-transfer-urls is missing or incomplete — transfer mark/unmark '
            + 'actions are disabled. Expected data-mark-url, data-unmark-url and data-reapply-url.'
        );
    } else if (!(tableWrapper as any)._transferActionBound) {
        (tableWrapper as any)._transferActionBound = true;

        tableWrapper.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest('.js-transfer-action') as HTMLButtonElement | null;
            if (!btn) return;

            const txnId = btn.dataset.txnId ?? '';
            const action = btn.dataset.action ?? '';
            const row = btn.closest('tr') as HTMLElement | null;

            function setRowDisabled(disabled: boolean): void {
                row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = disabled);
            }

            if (action === 'mark') {
                setRowDisabled(true);
                postTransferJson(markUrl, { txnId })
                    .then((resp: any) => {
                        if (!resp.success) {
                            setRowDisabled(false);
                            notifier.alert(resp.message ?? 'Something went wrong — please try again.');
                            return;
                        }
                        if (datatable) datatable.ajax.reload(null, false);
                        // D-10: dry-run figures already came back with the mark response
                        // (markAsTransfer() ran reapplyToHistory($ruleId, true) internally) —
                        // no separate preview call needed, just the confirm before committing.
                        const matchCount = resp.matchCount ?? 0;
                        if (matchCount > 0 && resp.ruleId) {
                            const matchTotal = resp.matchTotal ?? 0;
                            const proceed = confirm(
                                `This also matches ${matchCount} past transaction${matchCount !== 1 ? 's' : ''} `
                                + `totalling $${number_format(matchTotal, 2)} — apply to those too?`
                            );
                            if (proceed) {
                                // confirm:'1' is REQUIRED (CR-05) — handleReapplyTransferRule!
                                // now defaults to a dry run when the field is absent, so
                                // omitting it here would silently turn D-10's confirmed
                                // apply into a no-op. The user's confirmation is the
                                // window.confirm() directly above, driven by the dry-run
                                // figures markAsTransfer() already returned.
                                // reapplyUrl comes from .js-transfer-urls (WR-09) — this is a
                                // COMMIT, so it must never be a hardcoded relative '?do='.
                                postTransferJson(reapplyUrl, { ruleId: String(resp.ruleId), confirm: '1' })
                                    .then((r2: any) => {
                                        if (r2.success) {
                                            if (datatable) datatable.ajax.reload(null, false);
                                        } else {
                                            notifier.alert(r2.message ?? 'Something went wrong — please try again.');
                                        }
                                    })
                                    .catch(() => notifier.alert('Something went wrong — please try again.'));
                            }
                        }
                    })
                    .catch(() => {
                        setRowDisabled(false);
                        notifier.alert('Something went wrong — please try again.');
                    });
                return;
            }

            if (action === 'unmark') {
                // D-11's choice prompt — "just this row" vs "never for this merchant".
                // Two sequential native confirms (rather than a 3-way dialog) keep the
                // "cancel the whole unmark" and "scope of the unmark" questions unambiguous.
                if (!confirm('Unmark this transaction as a transfer?')) return;
                const neverForMerchant = confirm(
                    'Also stop auto-flagging future transactions from this merchant '
                    + '("never for this merchant")?\n\nOK = never for this merchant\nCancel = just this transaction'
                );

                setRowDisabled(true);
                postTransferJson(unmarkUrl, { txnId, neverForMerchant: neverForMerchant ? '1' : '0' })
                    .then((resp: any) => {
                        if (!resp.success) {
                            setRowDisabled(false);
                            notifier.alert(resp.message ?? 'Something went wrong — please try again.');
                            return;
                        }
                        if (datatable) datatable.ajax.reload(null, false);
                        // matchCount here counts OTHER currently-flagged rows sharing this
                        // merchant, regardless of which rule flagged them — there is no single
                        // ruleId this figure maps onto (unlike the mark flow's D-10 count), so
                        // it is surfaced informationally rather than wired to an automatic bulk
                        // un-apply (which operates per-rule, not per-merchant, and would
                        // silently no-op or under-apply against the wrong rule id). The
                        // per-rule path is the Rule Library's gated Delete button.
                        const matchCount = resp.matchCount ?? 0;
                        if (matchCount > 0) {
                            const matchTotal = resp.matchTotal ?? 0;
                            notifier.info(
                                `${matchCount} other transaction${matchCount !== 1 ? 's' : ''} from this merchant `
                                + `($${number_format(matchTotal, 2)}) ${matchCount !== 1 ? 'are' : 'is'} still flagged — `
                                + `review them on the Transfers page.`
                            );
                        }
                    })
                    .catch(() => {
                        setRowDisabled(false);
                        notifier.alert('Something went wrong — please try again.');
                    });
            }
        });
    }
}

// ── Phase 195-05 (D-06/BANK-05) — "Suspected duplicates" quarantine review panel ──
// Page-guarded to list.latte (returns early when the panel isn't present — e.g. every
// other Transactions sub-page). The panel is server-rendered only when $quarantined is
// non-empty (list.latte {if $quarantined}), so a fresh page load with 0 quarantined rows
// never even puts this markup in the DOM — this function just wires the buttons when it is.
function initQuarantinePanel(): void {
    const panel = document.querySelector('.js-quarantine-panel') as HTMLElement | null;
    if (!panel) return;

    const countBadge = panel.querySelector('.js-quarantine-count') as HTMLElement | null;

    // Removes the resolved row from the panel; once the last row is gone, the whole
    // panel disappears (D-11 low-noise — no empty "Suspected duplicates" card left behind).
    function removeRow(txnId: string): void {
        const row = panel!.querySelector(`tr[data-txn-id="${txnId}"]`) as HTMLElement | null;
        if (row) row.remove();
        const remaining = panel!.querySelectorAll('tbody tr').length;
        if (countBadge) countBadge.textContent = String(remaining);
        if (remaining === 0) panel!.remove();
    }

    // Idempotent — a single delegated listener guards against double-binding if this
    // module were ever re-invoked (shared-component double-bind memory).
    if ((panel as any)._quarantineBound) return;
    (panel as any)._quarantineBound = true;

    panel.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;

        const keepBtn = target.closest('.js-quarantine-keep') as HTMLButtonElement | null;
        if (keepBtn) {
            const txnId = keepBtn.dataset.txnId ?? '';
            const row = keepBtn.closest('tr') as HTMLElement | null;
            row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = true);

            const body = new URLSearchParams();
            body.set('txnId', txnId);

            fetch('?do=keepQuarantined', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    removeRow(txnId);
                    // Kept row now belongs in the main ledger — refresh so it appears without a manual reload.
                    if (datatable) datatable.ajax.reload(null, false);
                } else {
                    row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = false);
                    notifier.alert(data.message ?? 'Something went wrong — please try again.');
                }
            })
            .catch(() => {
                row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = false);
                notifier.alert('Something went wrong — please try again.');
            });
            return;
        }

        const discardBtn = target.closest('.js-quarantine-discard') as HTMLButtonElement | null;
        if (discardBtn) {
            const txnId = discardBtn.dataset.txnId ?? '';
            const row = discardBtn.closest('tr') as HTMLElement | null;
            row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = true);

            const body = new URLSearchParams();
            body.set('txnId', txnId);

            fetch('?do=discardQuarantined', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    removeRow(txnId);
                } else {
                    row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = false);
                    notifier.alert(data.message ?? 'Something went wrong — please try again.');
                }
            })
            .catch(() => {
                row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = false);
                notifier.alert('Something went wrong — please try again.');
            });
        }
    });
}

function undoButtons() {
    // Use event delegation on document so it works regardless of when the DOM renders.
    document.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const btn = target.closest('.js-undo-batch') as HTMLElement | null;
        if (!btn) return;

        const batchId = btn.getAttribute('data-batch-id');
        if (!batchId) return;

        if (!confirm(`Undo batch #${batchId}? All its transactions will be permanently removed.`)) {
            return;
        }

        btn.setAttribute('disabled', 'disabled');
        btn.textContent = 'Undoing…';

        const body = new URLSearchParams();
        body.set('batchId', batchId);

        fetch('?do=undoBatch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            const resultEl = document.querySelector('.js-undo-result') as HTMLElement | null;
            if (data.success) {
                // Remove the row from the recent-imports table
                const row = btn.closest('tr');
                if (row) row.remove();
                if (resultEl) {
                    resultEl.classList.remove('d-none');
                    resultEl.innerHTML = `<div class="alert alert-success">Batch #${batchId} undone — ${data.deleted} row${data.deleted !== 1 ? 's' : ''} removed.</div>`;
                }
                // Reload the DataTable to reflect deleted transactions
                if (typeof datatable !== 'undefined' && datatable) {
                    datatable.ajax.reload();
                }
            } else {
                btn.removeAttribute('disabled');
                btn.textContent = 'Undo';
                if (resultEl) {
                    resultEl.classList.remove('d-none');
                    resultEl.innerHTML = `<div class="alert alert-danger">Undo failed: ${data.message ?? 'Unknown error'}</div>`;
                }
            }
        })
        .catch(() => {
            btn.removeAttribute('disabled');
            btn.textContent = 'Undo';
            const resultEl = document.querySelector('.js-undo-result') as HTMLElement | null;
            if (resultEl) {
                resultEl.classList.remove('d-none');
                resultEl.innerHTML = '<div class="alert alert-danger">An error occurred while undoing the batch.</div>';
            }
        });
    });
}

function review() {
    const cardList = document.querySelector('#review-card-list') as HTMLElement | null;
    if (!cardList) return;

    // Helper: update nav badge spans to the given count; remove span entirely when 0
    function updateBadge(pendingCount: number): void {
        document.querySelectorAll('.nav-link .badge, .nav-tabs .badge, li > a > .badge').forEach((el) => {
            // Only update the Review badge — identified by nearest link containing "review"
            const link = el.closest('a');
            if (link && (link.href.includes('/transactions/review') || link.textContent?.toLowerCase().includes('review'))) {
                if (pendingCount === 0) {
                    el.remove();
                } else {
                    el.textContent = String(pendingCount);
                }
            }
        });
    }

    // Helper: vanish-animate a card then remove it from DOM
    function vanishCard(card: HTMLElement, callback?: () => void): void {
        card.setAttribute('aria-hidden', 'true');
        card.style.transition = 'opacity 0.3s, transform 0.3s';
        card.style.opacity = '0';
        card.style.transform = 'translateY(-8px)';
        setTimeout(() => {
            card.remove();
            if (callback) callback();
        }, 300);
    }

    // Helper: check if all cards are gone and show empty state
    function maybeShowEmptyState(pendingCount: number): void {
        const remaining = cardList.querySelectorAll('.review-card');
        if (remaining.length === 0 || pendingCount === 0) {
            // Show empty state if not already present
            if (!cardList.querySelector('.js-empty-state')) {
                const emptyDiv = document.createElement('div');
                emptyDiv.className = 'col-12 col-md-8 col-lg-6 mx-auto text-center py-5 js-empty-state';
                emptyDiv.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" class="bi bi-check-circle-fill text-success" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                    </svg>
                    <h5 class="mt-3 fw-semibold">All caught up</h5>
                    <p class="text-muted">No transactions need review right now. Import a CSV to add items.</p>`;
                cardList.appendChild(emptyDiv);
            }
        }
    }

    // Helper: update bulk bar count label + button text + show/hide
    function updateBulkBar(): void {
        const checked = cardList.querySelectorAll('.review-bulk-check:checked');
        const count = checked.length;
        const bar = document.querySelector('#review-bulk-bar') as HTMLElement | null;
        const countLabel = bar?.querySelector('.js-bulk-count-label');
        const confirmBtn = bar?.querySelector('.js-bulk-confirm');
        if (!bar) return;
        if (count === 0) {
            bar.classList.add('d-none');
            cardList.style.paddingBottom = '';
        } else {
            bar.classList.remove('d-none');
            cardList.style.paddingBottom = '80px';
        }
        if (countLabel) {
            countLabel.textContent = count === 1 ? '1 item selected' : `${count} items selected`;
        }
        if (confirmBtn) {
            confirmBtn.textContent = count === 1 ? 'Confirm 1 item' : `Confirm ${count} items`;
        }
    }

    // ── Category select: reveal / hide new-cat fields ──────────────────────
    cardList.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLElement;

        // Category select change
        if (target.classList.contains('review-cat-select')) {
            const card = target.closest('.review-card') as HTMLElement | null;
            if (!card) return;
            const newCatBlock = card.querySelector('.review-new-cat') as HTMLElement | null;
            const newCatName = card.querySelector('.review-new-cat-name') as HTMLInputElement | null;
            const newCatParent = card.querySelector('.review-new-cat-parent') as HTMLSelectElement | null;
            const select = target as HTMLSelectElement;
            if (select.value === '__new__') {
                newCatBlock?.classList.remove('d-none');
                if (newCatName) {
                    newCatName.required = true;
                    const suggestedName = card.dataset.suggestedName ?? '';
                    if (suggestedName) newCatName.value = suggestedName;
                    newCatName.focus();
                }
                if (newCatParent) newCatParent.required = true;
            } else {
                newCatBlock?.classList.add('d-none');
                if (newCatName) { newCatName.required = false; newCatName.value = ''; }
                if (newCatParent) newCatParent.required = false;
            }
        }

        // Save-as-rule checkbox change
        if (target.classList.contains('review-rule-check')) {
            const card = target.closest('.review-card') as HTMLElement | null;
            if (!card) return;
            const kwBlock = card.querySelector('.review-rule-keyword') as HTMLElement | null;
            const kwInput = card.querySelector('.review-rule-kw') as HTMLInputElement | null;
            const checkbox = target as HTMLInputElement;
            if (checkbox.checked) {
                kwBlock?.classList.remove('d-none');
                if (kwInput) {
                    kwInput.value = card.dataset.merchant ?? '';
                    kwInput.focus();
                }
            } else {
                kwBlock?.classList.add('d-none');
            }
        }

        // Bulk checkbox change
        if (target.classList.contains('review-bulk-check')) {
            const card = target.closest('.review-card') as HTMLElement | null;
            if (card) {
                const checkbox = target as HTMLInputElement;
                if (checkbox.checked) {
                    card.classList.add('review-bulk-selected');
                } else {
                    card.classList.remove('review-bulk-selected');
                }
            }
            updateBulkBar();
        }
    });

    // ── Confirm / Reject button clicks ────────────────────────────────────
    cardList.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;

        // Per-item Confirm
        const confirmBtn = target.closest('.review-confirm') as HTMLButtonElement | null;
        if (confirmBtn) {
            const card = confirmBtn.closest('.review-card') as HTMLElement | null;
            if (!card) return;

            const queueId = card.dataset.queueId ?? '';
            const catSelect = card.querySelector('.review-cat-select') as HTMLSelectElement | null;
            const subCatValue = catSelect?.value ?? '';
            const newCatName = (card.querySelector('.review-new-cat-name') as HTMLInputElement | null)?.value.trim() ?? '';
            const newCatParent = (card.querySelector('.review-new-cat-parent') as HTMLSelectElement | null)?.value ?? '';
            const saveRule = (card.querySelector('.review-rule-check') as HTMLInputElement | null)?.checked ? 1 : 0;
            const ruleKeyword = (card.querySelector('.review-rule-kw') as HTMLInputElement | null)?.value.trim() ?? '';

            // Validation
            const isNewCat = subCatValue === '__new__';
            let valid = true;
            if (!subCatValue) valid = false;
            if (isNewCat && (!newCatName || !newCatParent)) valid = false;

            // Remove any previous inline error
            card.querySelectorAll('.review-inline-error').forEach(el => el.remove());
            if (!valid) {
                const errDiv = document.createElement('div');
                errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                errDiv.textContent = 'Please select a category.';
                card.querySelector('.card-body')?.appendChild(errDiv);
                return;
            }

            card.classList.add('is-loading');

            // Remember-recipient fields (etransfer cards only — null-guarded for non-etransfer)
            const recipCheck = card.querySelector('.review-recipient-check') as HTMLInputElement | null;
            const saveRecipient = recipCheck?.checked ? 1 : 0;
            const recipientNote = (card.querySelector('.review-recipient-note-input') as HTMLInputElement | null)?.value ?? '';

            const body = new URLSearchParams();
            body.set('queueId', queueId);
            body.set('subCatId', isNewCat ? '0' : subCatValue);
            body.set('saveRule', String(saveRule));
            body.set('ruleKeyword', ruleKeyword);
            body.set('newCatName', isNewCat ? newCatName : '');
            body.set('newCatParentId', isNewCat ? newCatParent : '0');
            body.set('saveRecipient', String(saveRecipient));
            body.set('recipientNote', recipientNote);

            fetch('?do=confirmReview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    updateBadge(data.pendingCount);
                    vanishCard(card, () => maybeShowEmptyState(data.pendingCount));
                } else {
                    card.classList.remove('is-loading');
                    const errDiv = document.createElement('div');
                    errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                    errDiv.textContent = data.message ?? 'Something went wrong — please try again.';
                    card.querySelector('.card-footer')?.after(errDiv);
                }
            })
            .catch(() => {
                card.classList.remove('is-loading');
                const errDiv = document.createElement('div');
                errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                errDiv.textContent = 'Something went wrong — please try again.';
                card.querySelector('.card-footer')?.after(errDiv);
            });
        }

        // Per-item Reject
        const rejectBtn = target.closest('.review-reject') as HTMLButtonElement | null;
        if (rejectBtn) {
            const card = rejectBtn.closest('.review-card') as HTMLElement | null;
            if (!card) return;

            const queueId = card.dataset.queueId ?? '';
            card.classList.add('is-loading');

            const body = new URLSearchParams();
            body.set('queueId', queueId);

            fetch('?do=rejectReview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    updateBadge(data.pendingCount);
                    vanishCard(card, () => maybeShowEmptyState(data.pendingCount));
                } else {
                    card.classList.remove('is-loading');
                    const errDiv = document.createElement('div');
                    errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                    errDiv.textContent = data.message ?? 'Something went wrong — please try again.';
                    card.querySelector('.card-footer')?.after(errDiv);
                }
            })
            .catch(() => {
                card.classList.remove('is-loading');
                const errDiv = document.createElement('div');
                errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                errDiv.textContent = 'Something went wrong — please try again.';
                card.querySelector('.card-footer')?.after(errDiv);
            });
        }
    });

    // ── Bulk confirm button ───────────────────────────────────────────────
    const bulkBar = document.querySelector('#review-bulk-bar') as HTMLElement | null;
    bulkBar?.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const bulkBtn = target.closest('.js-bulk-confirm') as HTMLButtonElement | null;
        if (!bulkBtn) return;

        const checked = Array.from(cardList.querySelectorAll('.review-bulk-check:checked')) as HTMLInputElement[];
        if (checked.length === 0) return;

        const queueIds = checked.map(cb => cb.dataset.queueId ?? '').filter(Boolean).join(',');

        const body = new URLSearchParams();
        body.set('queueIds', queueIds);

        fetch('?do=bulkConfirmReview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                updateBadge(data.pendingCount);
                // Vanish all checked cards
                const checkedCards = checked.map(cb => cb.closest('.review-card') as HTMLElement | null).filter(Boolean) as HTMLElement[];
                let vanished = 0;
                checkedCards.forEach(card => {
                    vanishCard(card, () => {
                        vanished++;
                        if (vanished === checkedCards.length) {
                            maybeShowEmptyState(data.pendingCount);
                        }
                    });
                });
                // Hide the bulk bar
                bulkBar.classList.add('d-none');
                cardList.style.paddingBottom = '';
            } else {
                const errDiv = document.createElement('div');
                errDiv.className = 'alert alert-danger mt-2 py-1 small';
                errDiv.textContent = data.message ?? 'Something went wrong — please try again.';
                bulkBar.querySelector('.container')?.appendChild(errDiv);
            }
        })
        .catch(() => {
            const errDiv = document.createElement('div');
            errDiv.className = 'alert alert-danger mt-2 py-1 small';
            errDiv.textContent = 'Something went wrong — please try again.';
            bulkBar.querySelector('.container')?.appendChild(errDiv);
        });
    });
}

// ── Recipients management page (Transactions:recipients) ─────────────────────
// Guards: only binds when #recipients-table is in the DOM (project memory: index.ts
// runs all page init()s on every page — guard prevents double-bind on other pages).
// Pitfall: Shared-component init on every page can double-bind + fire wrong-presenter
// signals if not scoped (feedback_shared_component_every_page_init_double_bind).
function initRecipients(): void {
    const table = document.getElementById('recipients-table') as HTMLTableElement | null;
    if (!table) return;  // not on the recipients page

    // Read the categoryTree injected by the Latte template
    const categoryTree: { id: number; name: string; subs: { id: number; name: string }[] }[] =
        (window as any).categoryTree || [];

    // Helper: POST to a URL with a URLSearchParams body; returns parsed JSON
    function postJson(url: string, params: Record<string, string>): Promise<any> {
        const body = new URLSearchParams(params);
        return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        }).then(r => r.json());
    }

    // Helper: fade out + remove a <tr>
    function fadeRemoveRow(row: HTMLTableRowElement): void {
        row.style.transition = 'opacity 0.25s';
        row.style.opacity = '0';
        setTimeout(() => row.remove(), 260);
    }

    // Helper: build a grouped <select> from categoryTree, pre-selecting a given subCatId
    function buildCatSelect(currentSubCatId: number): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm js-recip-cat-select';
        categoryTree.forEach(main => {
            const og = document.createElement('optgroup');
            og.label = main.name;
            main.subs.forEach(sub => {
                const opt = document.createElement('option');
                opt.value = String(sub.id);
                opt.textContent = sub.name;
                if (sub.id === currentSubCatId) opt.selected = true;
                og.appendChild(opt);
            });
            sel.appendChild(og);
        });
        return sel;
    }

    // ── Edit flow ─────────────────────────────────────────────────────────────
    // Click Edit → reveal hidden edit sub-row with prefilled form
    table.addEventListener('click', function(e: Event) {
        const editBtn = (e.target as HTMLElement).closest('.js-recip-edit-btn') as HTMLElement | null;
        if (!editBtn) return;

        const recipId    = editBtn.dataset.recipId || '';
        const subCatId   = parseInt(editBtn.dataset.subCatId || '0', 10);
        const desc       = editBtn.dataset.description || '';
        const editUrl    = editBtn.dataset.url || '';
        const dataRow    = table.querySelector(`tr.js-recip-row[data-recip-id="${recipId}"]`) as HTMLTableRowElement | null;
        const editRow    = table.querySelector(`tr.js-recip-edit-row[data-recip-id="${recipId}"]`) as HTMLTableRowElement | null;
        if (!dataRow || !editRow) return;

        // Prefill and reveal the edit row
        const catSelectContainer = editRow.querySelector('.js-recip-cat-select-container') as HTMLElement | null;
        if (catSelectContainer) {
            catSelectContainer.innerHTML = '';
            catSelectContainer.appendChild(buildCatSelect(subCatId));
        }
        const descInput = editRow.querySelector('.js-recip-desc-input') as HTMLInputElement | null;
        if (descInput) descInput.value = desc;

        editRow.classList.remove('d-none');
        editBtn.disabled = true;

        // Save handler
        const saveBtn = editRow.querySelector('.js-recip-save-btn') as HTMLButtonElement | null;
        const cancelBtn = editRow.querySelector('.js-recip-cancel-btn') as HTMLButtonElement | null;
        const errDiv  = dataRow.querySelector('.js-recip-error') as HTMLElement | null;

        function collapseEdit(): void {
            editRow!.classList.add('d-none');
            editBtn.disabled = false;
        }

        if (cancelBtn) {
            cancelBtn.onclick = () => collapseEdit();
        }

        if (saveBtn) {
            saveBtn.onclick = () => {
                const selectedCatSelect = editRow.querySelector('.js-recip-cat-select') as HTMLSelectElement | null;
                const newSubCatId  = selectedCatSelect?.value || '';
                const newDesc      = descInput?.value ?? '';

                saveBtn.disabled = true;
                saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
                if (errDiv) { errDiv.textContent = ''; errDiv.classList.add('d-none'); }

                postJson(editUrl, { id: recipId, subCatId: newSubCatId, description: newDesc })
                    .then((data: any) => {
                        if (data.success) {
                            // Update the visible data row cells in-place
                            // Find the category name from the tree
                            let newMainName = '';
                            let newSubName  = '';
                            const newSubCatIdNum = parseInt(newSubCatId, 10);
                            for (const main of categoryTree) {
                                for (const sub of main.subs) {
                                    if (sub.id === newSubCatIdNum) {
                                        newMainName = main.name;
                                        newSubName  = sub.name;
                                    }
                                }
                            }

                            const catCell = dataRow.querySelector('.js-recip-cat-cell') as HTMLElement | null;
                            if (catCell) catCell.innerHTML = `<span class="text-muted small">${newMainName}</span> › ${newSubName}`;
                            const descCell = dataRow.querySelector('.js-recip-desc-cell') as HTMLElement | null;
                            if (descCell) descCell.textContent = newDesc !== '' ? newDesc : '—';

                            // Update data attributes on the edit button for next time
                            editBtn.dataset.subCatId   = newSubCatId;
                            editBtn.dataset.description = newDesc;

                            collapseEdit();
                        } else {
                            saveBtn.disabled = false;
                            saveBtn.textContent = 'Save';
                            if (errDiv) {
                                errDiv.textContent = 'Failed: ' + (data.message || 'Unknown error') + '. Try again.';
                                errDiv.classList.remove('d-none');
                            }
                        }
                    })
                    .catch(() => {
                        saveBtn.disabled = false;
                        saveBtn.textContent = 'Save';
                        if (errDiv) {
                            errDiv.textContent = 'Failed: network error. Try again.';
                            errDiv.classList.remove('d-none');
                        }
                    });
            };
        }
    });

    // ── Re-apply flow (two-step: dry-run → confirm) ───────────────────────────
    table.addEventListener('click', function(e: Event) {
        const reapplyBtn = (e.target as HTMLElement).closest('.js-recip-reapply-btn') as HTMLButtonElement | null;
        if (!reapplyBtn) return;

        const recipId   = reapplyBtn.dataset.recipId  || '';
        const recipient = reapplyBtn.dataset.recipient || '';
        const reapplyUrl = reapplyBtn.dataset.url || '';
        const td         = reapplyBtn.closest('td') as HTMLTableCellElement | null;
        const dataRow    = reapplyBtn.closest('tr.js-recip-row') as HTMLTableRowElement | null;
        const errDiv     = dataRow?.querySelector('.js-recip-error') as HTMLElement | null;
        if (!td) return;

        // Save original button group HTML for restore
        const originalHtml = td.innerHTML;

        reapplyBtn.disabled = true;
        reapplyBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Checking…';
        if (errDiv) { errDiv.textContent = ''; errDiv.classList.add('d-none'); }

        postJson(reapplyUrl, { id: recipId, dryRun: '1' })
            .then((data: any) => {
                if (!data.success) {
                    td.innerHTML = originalHtml;
                    if (errDiv) {
                        errDiv.textContent = 'Failed: ' + (data.message || 'Unknown error') + '. Try again.';
                        errDiv.classList.remove('d-none');
                    }
                    return;
                }
                const count = data.affected as number;

                if (count === 0) {
                    // Zero result — show info + close
                    td.innerHTML = `
                        <span class="text-muted small">No matching past transactions found.</span>
                        <button class="btn btn-sm btn-link js-recip-reapply-close ms-1">Close</button>`;
                    td.querySelector('.js-recip-reapply-close')?.addEventListener('click', () => {
                        td.innerHTML = originalHtml;
                    });
                } else {
                    // Show confirm prompt
                    td.innerHTML = `
                        <span class="text-muted small">Re-categorize <strong>${count}</strong> past transactions?</span>
                        <button class="btn btn-sm btn-primary js-recip-reapply-confirm ms-1">Confirm</button>
                        <button class="btn btn-sm btn-link js-recip-reapply-cancel">Cancel</button>`;

                    td.querySelector('.js-recip-reapply-cancel')?.addEventListener('click', () => {
                        td.innerHTML = originalHtml;
                    });

                    const confirmBtn = td.querySelector('.js-recip-reapply-confirm') as HTMLButtonElement | null;
                    if (confirmBtn) {
                        confirmBtn.addEventListener('click', () => {
                            confirmBtn.disabled = true;
                            confirmBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

                            postJson(reapplyUrl, { id: recipId })
                                .then((d2: any) => {
                                    if (d2.success) {
                                        const applied = d2.affected as number;
                                        td.innerHTML = `<span class="text-success small">&#10003; ${applied} transaction${applied !== 1 ? 's' : ''} re-categorized.</span>`;
                                        setTimeout(() => { td.innerHTML = originalHtml; }, 3000);
                                    } else {
                                        td.innerHTML = originalHtml;
                                        if (errDiv) {
                                            errDiv.textContent = 'Failed: ' + (d2.message || 'Unknown error') + '. Try again.';
                                            errDiv.classList.remove('d-none');
                                        }
                                    }
                                })
                                .catch(() => {
                                    td.innerHTML = originalHtml;
                                    if (errDiv) {
                                        errDiv.textContent = 'Failed: network error. Try again.';
                                        errDiv.classList.remove('d-none');
                                    }
                                });
                        });
                    }
                }
            })
            .catch(() => {
                td.innerHTML = originalHtml;
                if (errDiv) {
                    errDiv.textContent = 'Failed: network error. Try again.';
                    errDiv.classList.remove('d-none');
                }
            });
    });

    // ── Delete flow (inline confirm → fade remove) ────────────────────────────
    table.addEventListener('click', function(e: Event) {
        const deleteBtn = (e.target as HTMLElement).closest('.js-recip-delete-btn') as HTMLButtonElement | null;
        if (!deleteBtn) return;

        const recipId   = deleteBtn.dataset.recipId  || '';
        const recipient = deleteBtn.dataset.recipient || '';
        const deleteUrl  = deleteBtn.dataset.url || '';
        const td         = deleteBtn.closest('td') as HTMLTableCellElement | null;
        const dataRow    = deleteBtn.closest('tr.js-recip-row') as HTMLTableRowElement | null;
        const editRow    = dataRow ? (table.querySelector(`tr.js-recip-edit-row[data-recip-id="${recipId}"]`) as HTMLTableRowElement | null) : null;
        const errDiv     = dataRow?.querySelector('.js-recip-error') as HTMLElement | null;
        if (!td || !dataRow) return;

        const originalHtml = td.innerHTML;

        // Show inline confirm
        td.innerHTML = `
            <span class="text-muted small">Remove <strong>${recipient}</strong>?</span>
            <button class="btn btn-sm btn-danger js-recip-delete-confirm ms-1">Yes, delete</button>
            <button class="btn btn-sm btn-link js-recip-delete-cancel">Cancel</button>`;

        td.querySelector('.js-recip-delete-cancel')?.addEventListener('click', () => {
            td.innerHTML = originalHtml;
        });

        const confirmBtn = td.querySelector('.js-recip-delete-confirm') as HTMLButtonElement | null;
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => {
                confirmBtn.disabled = true;
                confirmBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

                postJson(deleteUrl, { id: recipId })
                    .then((data: any) => {
                        if (data.success) {
                            // Fade out both data row + edit sub-row
                            if (editRow) {
                                editRow.style.transition = 'opacity 0.25s';
                                editRow.style.opacity = '0';
                                setTimeout(() => editRow.remove(), 260);
                            }
                            fadeRemoveRow(dataRow);
                        } else {
                            td.innerHTML = originalHtml;
                            if (errDiv) {
                                errDiv.textContent = 'Failed: ' + (data.message || 'Unknown error') + '. Try again.';
                                errDiv.classList.remove('d-none');
                            }
                        }
                    })
                    .catch(() => {
                        td.innerHTML = originalHtml;
                        if (errDiv) {
                            errDiv.textContent = 'Failed: network error. Try again.';
                            errDiv.classList.remove('d-none');
                        }
                    });
            });
        }
    });
}

// ── Transfers page (Transactions:transfers) — D-14 excluded rows + rule library ──
// Guards: only binds when the year-select control is in the DOM (project memory: index.ts
// runs all page init()s on every page — guard prevents double-bind on other pages, matching
// initQuarantinePanel()'s early `if (!el) return;` shape).
function initTransfersPage(): void {
    const pageMarker = document.getElementById('js-transfers-year-select');
    if (!pageMarker) return;  // not on the transfers page
    if ((pageMarker as any)._transfersPageBound) return;  // idempotent — no double-binding
    (pageMarker as any)._transfersPageBound = true;

    const container = pageMarker.closest('.container') as HTMLElement | null;
    if (!container) return;

    container.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;

        // ── Unmark an excluded row (same D-11 two-choice flow as the ledger) ───────
        const unmarkBtn = target.closest('.js-transfer-unmark') as HTMLButtonElement | null;
        if (unmarkBtn) {
            const txnId = unmarkBtn.dataset.txnId ?? '';
            const url = unmarkBtn.dataset.url ?? '';
            const row = unmarkBtn.closest('tr.js-transfer-excluded-row') as HTMLTableRowElement | null;
            const errDiv = row?.querySelector('.js-transfer-row-error') as HTMLElement | null;

            if (!confirm('Unmark this transaction as a transfer?')) return;
            const neverForMerchant = confirm(
                'Also stop auto-flagging future transactions from this merchant '
                + '("never for this merchant")?\n\nOK = never for this merchant\nCancel = just this transaction'
            );

            row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = true);
            if (errDiv) { errDiv.textContent = ''; errDiv.classList.add('d-none'); }

            postTransferJson(url, { txnId, neverForMerchant: neverForMerchant ? '1' : '0' })
                .then((resp: any) => {
                    if (!resp.success) {
                        row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = false);
                        if (errDiv) {
                            errDiv.textContent = 'Failed: ' + (resp.message || 'Unknown error') + '. Try again.';
                            errDiv.classList.remove('d-none');
                        }
                        return;
                    }
                    if (row) {
                        row.style.transition = 'opacity 0.25s';
                        row.style.opacity = '0';
                        setTimeout(() => row.remove(), 260);
                    }
                    // See the ledger's unmark handler above for why this is informational
                    // only (no automatic bulk un-apply call).
                    const matchCount = resp.matchCount ?? 0;
                    if (matchCount > 0) {
                        const matchTotal = resp.matchTotal ?? 0;
                        notifier.info(
                            `${matchCount} other transaction${matchCount !== 1 ? 's' : ''} from this merchant `
                            + `($${number_format(matchTotal, 2)}) ${matchCount !== 1 ? 'are' : 'is'} still flagged.`
                        );
                    }
                })
                .catch(() => {
                    row?.querySelectorAll('button').forEach((b) => (b as HTMLButtonElement).disabled = false);
                    if (errDiv) {
                        errDiv.textContent = 'Failed: network error. Try again.';
                        errDiv.classList.remove('d-none');
                    }
                });
            return;
        }

        // ── Reveal the hidden inline edit sub-row (recipients.latte's edit-toggle shape) ──
        const editBtn = target.closest('.js-transfer-rule-edit') as HTMLButtonElement | null;
        if (editBtn) {
            const ruleId       = editBtn.dataset.ruleId ?? '';
            const label        = editBtn.dataset.label ?? '';
            const polarity     = editBtn.dataset.polarity ?? 'include';
            const url          = editBtn.dataset.url ?? '';
            const dataRow = container.querySelector(`tr.js-transfer-rule-row[data-rule-id="${ruleId}"]`) as HTMLTableRowElement | null;
            const editRow = container.querySelector(`tr.js-transfer-rule-edit-row[data-rule-id="${ruleId}"]`) as HTMLTableRowElement | null;
            if (!dataRow || !editRow) return;

            const labelInput    = editRow.querySelector('.js-transfer-rule-label-input') as HTMLInputElement | null;
            const polaritySelect = editRow.querySelector('.js-transfer-rule-polarity-select') as HTMLSelectElement | null;
            if (labelInput) labelInput.value = label;
            if (polaritySelect) polaritySelect.value = polarity;

            editRow.classList.remove('d-none');
            editBtn.disabled = true;

            const saveBtn   = editRow.querySelector('.js-transfer-rule-save-btn') as HTMLButtonElement | null;
            const cancelBtn = editRow.querySelector('.js-transfer-rule-cancel-btn') as HTMLButtonElement | null;
            const errDiv    = dataRow.querySelector('.js-transfer-rule-error') as HTMLElement | null;

            function collapseEdit(): void {
                editRow!.classList.add('d-none');
                editBtn!.disabled = false;
            }

            if (cancelBtn) cancelBtn.onclick = () => collapseEdit();

            if (saveBtn) {
                // WR-14 — restoring the button is an obligation of ALL THREE outcomes, not
                // just the two failure paths. The edit sub-row is server-rendered once per
                // rule and only hidden/shown (collapseEdit() just toggles d-none), so a
                // success path that left the button disabled with a spinner in it made a
                // second edit of the same rule impossible without a full page reload — and
                // the only affordance said nothing was wrong. Shaped after
                // restoreReapplyBtn() below.
                function restoreSaveBtn(): void {
                    saveBtn!.disabled = false;
                    saveBtn!.textContent = 'Save';
                }

                saveBtn.onclick = () => {
                    const newLabel    = labelInput?.value ?? '';
                    const newPolarity = polaritySelect?.value ?? 'include';

                    saveBtn.disabled = true;
                    saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
                    if (errDiv) { errDiv.textContent = ''; errDiv.classList.add('d-none'); }

                    postTransferJson(url, { ruleId, label: newLabel, polarity: newPolarity })
                        .then((resp: any) => {
                            if (resp.success) {
                                const labelCell = dataRow!.querySelector('.js-transfer-rule-label-cell') as HTMLElement | null;
                                if (labelCell) labelCell.textContent = newLabel !== '' ? newLabel : '—';
                                const polarityCell = dataRow!.querySelector('.js-transfer-rule-polarity-cell') as HTMLElement | null;
                                if (polarityCell) {
                                    polarityCell.innerHTML = newPolarity === 'exclude'
                                        ? '<span class="badge bg-danger bg-opacity-10 text-danger">exclude</span>'
                                        : '<span class="badge bg-success bg-opacity-10 text-success">include</span>';
                                }
                                // CR-02: an 'exclude' rule suppresses flags, so re-applying
                                // it can never create any. Keep the Re-apply button's
                                // visibility in sync with the polarity just saved, matching
                                // what transfers.latte renders on a fresh page load.
                                const reapplyBtn = dataRow!.querySelector('.js-transfer-rule-reapply') as HTMLElement | null;
                                if (reapplyBtn) reapplyBtn.classList.toggle('d-none', newPolarity === 'exclude');
                                editBtn.dataset.label    = newLabel;
                                editBtn.dataset.polarity = newPolarity;
                                restoreSaveBtn();
                                collapseEdit();
                            } else {
                                restoreSaveBtn();
                                if (errDiv) {
                                    errDiv.textContent = 'Failed: ' + (resp.message || 'Unknown error') + '. Try again.';
                                    errDiv.classList.remove('d-none');
                                }
                            }
                        })
                        .catch(() => {
                            restoreSaveBtn();
                            if (errDiv) {
                                errDiv.textContent = 'Failed: network error. Try again.';
                                errDiv.classList.remove('d-none');
                            }
                        });
                };
            }
            return;
        }

        // ── Re-apply a rule to history — D-07/D-10's two-step dry-run/confirm gate, one
        // signal keyed by the confirm POST field, identical in shape to Delete below.
        // The first call (confirm=0) writes NOTHING; it returns the count/total the
        // confirmation prompt shows. Only after the operator accepts does the second
        // call (confirm=1) commit. Re-applying flags N historical transactions, nulls
        // their category/group and removes their amounts from every spend total, chart
        // and budget — and there is no undo (CR-05). ────────────────────────────────
        const reapplyBtn = target.closest('.js-transfer-rule-reapply') as HTMLButtonElement | null;
        if (reapplyBtn) {
            const ruleId = reapplyBtn.dataset.ruleId ?? '';
            const url    = reapplyBtn.dataset.url ?? '';
            const originalText = reapplyBtn.textContent;
            const ruleRow = reapplyBtn.closest('tr.js-transfer-rule-row') as HTMLTableRowElement | null;
            const pattern = (ruleRow?.querySelector('td') as HTMLElement | null)?.textContent?.trim() ?? '';

            function restoreReapplyBtn(): void {
                reapplyBtn!.disabled = false;
                reapplyBtn!.textContent = originalText;
            }

            reapplyBtn.disabled = true;
            reapplyBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

            postTransferJson(url, { ruleId, confirm: '0' })
                .then((resp: any) => {
                    restoreReapplyBtn();
                    if (!resp.success) {
                        notifier.alert(resp.message ?? 'Something went wrong — please try again.');
                        return;
                    }
                    const count = resp.count ?? 0;
                    const total = resp.total ?? 0;
                    if (count === 0) {
                        notifier.info('Nothing to re-apply — no additional matching transactions found.');
                        return;
                    }
                    const sample: string[] = Array.isArray(resp.sample) ? resp.sample : [];
                    const proceed = confirm(
                        `Re-apply rule${pattern ? ` "${pattern}"` : ''}? This flags ${count} transaction${count !== 1 ? 's' : ''} `
                        + `totalling $${number_format(total, 2)} as transfers and removes them from every spend total `
                        + `— there is no undo.`
                        + (sample.length > 0 ? `\n\nFor example:\n${sample.slice(0, 5).join('\n')}` : '')
                    );
                    if (!proceed) return;

                    reapplyBtn.disabled = true;
                    reapplyBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
                    postTransferJson(url, { ruleId, confirm: '1' })
                        .then((resp2: any) => {
                            restoreReapplyBtn();
                            if (resp2.success) {
                                const c2 = resp2.count ?? 0;
                                const t2 = resp2.total ?? 0;
                                notifier.success(
                                    c2 > 0
                                        ? `Re-applied — ${c2} transaction${c2 !== 1 ? 's' : ''} totalling $${number_format(t2, 2)} flagged.`
                                        : 'Re-applied — no additional matching transactions found.'
                                );
                                // IN-10 — a successful re-apply changes the excluded-total card,
                                // the excluded-rows table AND the rule's Hits column: more surfaces
                                // than the delete flow's DOM-surgery touches, and leaving any one
                                // of the three stale is worse than the current honest staleness.
                                // Full reload after the toast (goals.ts/mood.ts page-reload
                                // pattern), success-only — never on the failure or .catch() branch,
                                // and never on the confirm=0 dry-run call above.
                                //
                                // IN-07 (281-18): this ACCEPTS discarding the operator's year
                                // filter, scroll position and any open DataTables state (sort/page/
                                // search) — the neighbouring delete flow does targeted DOM surgery
                                // instead precisely to avoid that cost. Accepted here because a
                                // successful re-apply changes three surfaces at once (see above),
                                // and leaving any one of them stale is worse than the reload's
                                // state loss.
                                setTimeout(() => window.location.reload(), TOAST_THEN_RELOAD_MS);
                            } else {
                                notifier.alert(resp2.message ?? 'Something went wrong — please try again.');
                            }
                        })
                        .catch(() => {
                            restoreReapplyBtn();
                            notifier.alert('Something went wrong — please try again.');
                        });
                })
                .catch(() => {
                    restoreReapplyBtn();
                    notifier.alert('Something went wrong — please try again.');
                });
            return;
        }

        // ── Delete a rule — D-15's two-step dry-run/confirm gate, one signal keyed by
        // the confirm POST field. The first call (confirm=0) writes nothing. ──────────
        const deleteBtn = target.closest('.js-transfer-rule-delete') as HTMLButtonElement | null;
        if (deleteBtn) {
            const ruleId  = deleteBtn.dataset.ruleId ?? '';
            const pattern = deleteBtn.dataset.pattern ?? '';
            const url     = deleteBtn.dataset.url ?? '';
            const dataRow = deleteBtn.closest('tr.js-transfer-rule-row') as HTMLTableRowElement | null;
            const editRow = container.querySelector(`tr.js-transfer-rule-edit-row[data-rule-id="${ruleId}"]`) as HTMLTableRowElement | null;
            const errDiv  = dataRow?.querySelector('.js-transfer-rule-error') as HTMLElement | null;

            deleteBtn.disabled = true;

            postTransferJson(url, { ruleId, confirm: '0' })
                .then((resp: any) => {
                    if (!resp.success) {
                        deleteBtn.disabled = false;
                        if (errDiv) {
                            errDiv.textContent = 'Failed: ' + (resp.message || 'Unknown error') + '. Try again.';
                            errDiv.classList.remove('d-none');
                        }
                        return;
                    }
                    const count = resp.count ?? 0;
                    const total = resp.total ?? 0;
                    const proceed = confirm(
                        count > 0
                            ? `Delete rule "${pattern}"? This un-flags ${count} transaction${count !== 1 ? 's' : ''} `
                              + `totalling $${number_format(total, 2)} — proceed?`
                            : `Delete rule "${pattern}"? No currently-flagged transactions reference it.`
                    );
                    if (!proceed) {
                        deleteBtn.disabled = false;
                        return;
                    }
                    postTransferJson(url, { ruleId, confirm: '1' })
                        .then((resp2: any) => {
                            // CR-08: `deleted` is the server's explicit statement that the
                            // txn_transfer_memory row is gone. Removing the DOM rows on
                            // `success` alone is what let a zero-hit rule "disappear" from
                            // the Rule Library and come back on the next reload. `!== false`
                            // (rather than `=== true`) keeps an older server response, which
                            // carries no `deleted` key at all, behaving as before.
                            if (resp2.success && resp2.deleted !== false) {
                                if (editRow) {
                                    editRow.style.transition = 'opacity 0.25s';
                                    editRow.style.opacity = '0';
                                    setTimeout(() => editRow.remove(), 260);
                                }
                                if (dataRow) {
                                    dataRow.style.transition = 'opacity 0.25s';
                                    dataRow.style.opacity = '0';
                                    setTimeout(() => dataRow.remove(), 260);
                                }
                            } else {
                                deleteBtn.disabled = false;
                                if (errDiv) {
                                    errDiv.textContent = resp2.success
                                        ? 'Failed: the rule was not deleted. Reload the page and try again.'
                                        : 'Failed: ' + (resp2.message || 'Unknown error') + '. Try again.';
                                    errDiv.classList.remove('d-none');
                                }
                            }
                        })
                        .catch(() => {
                            deleteBtn.disabled = false;
                            if (errDiv) {
                                errDiv.textContent = 'Failed: network error. Try again.';
                                errDiv.classList.remove('d-none');
                            }
                        });
                })
                .catch(() => {
                    deleteBtn.disabled = false;
                    if (errDiv) {
                        errDiv.textContent = 'Failed: network error. Try again.';
                        errDiv.classList.remove('d-none');
                    }
                });
        }
    });
}

// ── Subscriptions page (Transactions:subscriptions) ──────────────────────────
// Guards: only binds when the subscriptions page's table element is in the DOM
// (project memory: index.ts runs all page init()s on every page — guard prevents
// double-bind on other pages).
// Pitfall: Shared-component init on every page can double-bind + fire wrong-presenter
// signals if not scoped (feedback_shared_component_every_page_init_double_bind).
// Function name kept as initRecurring() (Phase 261-09 intentional non-change, see
// 261-09-SUMMARY.md) — internal identifier only, no user-visible surface.
function initRecurring(): void {
    const table = document.getElementById('subscriptions-table') as HTMLTableElement | null;
    if (!table) return;  // not on the subscriptions page — no-op guard

    // WR-03 fix (261-REVIEW.md) — built BEFORE `new DataTable(...)` below, while <tbody> still
    // holds every server-rendered row. DataTables' first draw runs synchronously on
    // construction and PHYSICALLY replaces <tbody>'s children with only the current page's rows
    // (confirmed against the vendor source, dataTables.js's _fnDraw():
    // `body[0].replaceChildren.apply(body[0], anRows)`) — rows outside the visible page are
    // detached from the live DOM, not merely hidden. Populating these maps AFTER construction
    // (as this code originally did, immediately before the modal wiring below) silently dropped
    // every norm_key beyond page 1: the Overdue band's "Fix prediction" would fall back to a
    // default Monthly interval, and the edit modal's "Clear override" would default is_manual to
    // false — both wrong for any subscription sorted past the first 25 rows (Est. Monthly DESC,
    // so low-cost multi-year subs — D-14's manual-add motivation — are the most likely to fall
    // off page 1). See the "Handoff from Plan 07" comment further below for why a norm_key-keyed
    // lookup against these two button sets is otherwise structurally guaranteed to resolve.
    const subIntervalByKey = new Map<string, string>();
    const subManualByKey   = new Map<string, boolean>();
    document.querySelectorAll<HTMLButtonElement>('#subscriptions-table [data-action="edit-sub"]').forEach(editBtn => {
        const nk = editBtn.dataset.normKey;
        if (nk) subIntervalByKey.set(nk, editBtn.dataset.interval ?? '');
    });
    document.querySelectorAll<HTMLButtonElement>('#subscriptions-table [data-action="remove-sub"]').forEach(rmBtn => {
        const nk = rmBtn.dataset.normKey;
        if (nk) subManualByKey.set(nk, rmBtn.dataset.manual === '1');
    });

    // Initialize the client-side DataTable on the table above.
    // Columns (Phase 193 inserted "Price Δ" at index 3, shifting Last Charge/Est. Monthly):
    // 0 Merchant, 1 Cadence, 2 Amount, 3 Price Δ, 4 Last Charge, 5 Est. Monthly.
    // Default sort: col 5 (Est. Monthly) descending — largest monthly cost first (D-12).
    // No ajax: config — data is server-rendered in the Latte template.
    new DataTable(table, {
        pageLength: 25,
        order: [[5, 'desc']] as Order[],
        columnDefs: [
            { targets: [2], className: 'text-end' },  // Amount
            { targets: [3], className: 'text-end' },  // Price Δ (Phase 193)
            { targets: [5], className: 'text-end' },  // Est. Monthly
        ],
    });

    // Initialize Bootstrap tooltips on merchant cells (D-01 reviewable surface).
    // Tooltips show raw name variants on hover/focus (long-press on mobile).
    // Import the Tooltip class directly — Bootstrap is NOT on window in this project
    // (feedback_bootstrap_not_on_window_use_import; same pattern as `import {Modal}` above).
    document.querySelectorAll('#subscriptions-table [data-bs-toggle="tooltip"]').forEach(el => {
        // Only init where there's actual tooltip content — Bootstrap 5 Tooltip throws
        // "Option title provided type null" on title-less elements (merchants with no
        // raw-name variants render the trigger but an empty title). Guard, don't throw.
        const title = el.getAttribute('title')
            || el.getAttribute('data-bs-title')
            || el.getAttribute('data-bs-original-title');
        if (title) new Tooltip(el);
    });

    // ── Shared override/manual-add modal wiring (Phase 261 Plan 08, D-13/D-19) ──
    // #subscriptionEditModal serves Add / Edit / Fix-prediction; #subscriptionRemoveModal
    // is the state-aware clear/delete confirmation both the modal's own "Clear override"
    // link and the table's dedicated "Remove" row action open. Both modals are
    // server-rendered inside this page (not injected), so getOrCreateInstance() is safe to
    // call once here — no risk of double-instantiation.
    const subEditModalEl    = document.getElementById('subscriptionEditModal');
    const subRemoveModalEl  = document.getElementById('subscriptionRemoveModal');
    // Phase 261.1 Plan 09 — Dismiss/Archive confirm modals (D-24/D-26). Guarded into the same
    // `if` as the two existing modals below: all four are server-rendered unconditionally on
    // this page, so a missing one is a template regression worth no-op'ing the whole block for,
    // exactly like the original two-modal guard already did.
    const subDismissModalEl = document.getElementById('subscriptionDismissModal');
    const subArchiveModalEl = document.getElementById('subscriptionArchiveModal');
    // 261.1-11 (D-41) — the adopt-history preview-then-confirm modal. Same guard shape: all
    // five modals are server-rendered unconditionally on this page.
    const subAdoptModalEl   = document.getElementById('subscriptionAdoptModal');
    if (subEditModalEl && subRemoveModalEl && subDismissModalEl && subArchiveModalEl && subAdoptModalEl) {
        const subEditModal    = Modal.getOrCreateInstance(subEditModalEl);
        const subRemoveModal  = Modal.getOrCreateInstance(subRemoveModalEl);
        const subDismissModal = Modal.getOrCreateInstance(subDismissModalEl);
        const subArchiveModal = Modal.getOrCreateInstance(subArchiveModalEl);
        const subAdoptModal   = Modal.getOrCreateInstance(subAdoptModalEl);

        const subModalLabel    = document.getElementById('subscriptionEditModalLabel');
        const subKeyInput      = document.getElementById('subscription-modal-key') as HTMLInputElement | null;
        const subChildIdInput  = document.getElementById('subscription-modal-child-id') as HTMLInputElement | null;
        const subAddFlagInput  = document.getElementById('subscription-modal-add') as HTMLInputElement | null;
        const subNameInput     = document.getElementById('subscription-modal-name') as HTMLInputElement | null;
        const subNameWarning   = document.getElementById('subscription-modal-name-warning');
        const subMerchantSelect = document.getElementById('subscription-modal-merchant') as HTMLSelectElement | null;
        const subAnchorInput   = document.getElementById('subscription-modal-anchor') as HTMLInputElement | null;
        const subIntervalSel   = document.getElementById('subscription-modal-interval') as HTMLSelectElement | null;
        const subYearsWrap     = document.getElementById('subscription-modal-years-wrap');
        const subYearsInput    = document.getElementById('subscription-modal-years') as HTMLInputElement | null;
        const subAmountInput   = document.getElementById('subscription-modal-amount') as HTMLInputElement | null;
        const subErrorEl       = document.getElementById('subscription-modal-error');
        const subClearBtn      = document.getElementById('subscription-modal-clear');
        const subSaveBtn       = document.getElementById('subscription-modal-save') as HTMLButtonElement | null;

        const subRemoveLabel      = document.getElementById('subscriptionRemoveModalLabel');
        const subRemoveBody       = document.getElementById('subscription-remove-body');
        const subRemoveKeyInput   = document.getElementById('subscription-remove-key') as HTMLInputElement | null;
        const subRemoveChildIdInput = document.getElementById('subscription-remove-child-id') as HTMLInputElement | null;
        const subRemoveConfirmBtn = document.getElementById('subscription-remove-confirm') as HTMLButtonElement | null;

        // D-24 — Dismiss modal elements.
        const subDismissBody       = document.getElementById('subscription-dismiss-body');
        const subDismissKeyInput   = document.getElementById('subscription-dismiss-key') as HTMLInputElement | null;
        const subDismissConfirmBtn = document.getElementById('subscription-dismiss-confirm') as HTMLButtonElement | null;

        // D-26 — Archive modal elements.
        const subArchiveLabel        = document.getElementById('subscriptionArchiveModalLabel');
        const subArchiveNormKeyInput = document.getElementById('subscription-archive-norm-key') as HTMLInputElement | null;
        const subArchiveChildIdInput = document.getElementById('subscription-archive-child-id') as HTMLInputElement | null;
        const subArchiveConfirmBtn   = document.getElementById('subscription-archive-confirm') as HTMLButtonElement | null;

        // D-41 — Adopt-history preview modal elements.
        const subAdoptBody            = document.getElementById('subscription-adopt-body');
        const subAdoptUnattributable  = document.getElementById('subscription-adopt-unattributable');
        const subAdoptChildIdInput    = document.getElementById('subscription-adopt-child-id') as HTMLInputElement | null;
        const subAdoptConfirmBtn      = document.getElementById('subscription-adopt-confirm') as HTMLButtonElement | null;

        // Handoff from Plan 07 (see 261-07-SUMMARY.md): the Overdue band's "Fix prediction"
        // button carries an EMPTY data-interval — buildUpcomingZone()'s zone-row shape has no
        // interval_days property at all (confirmed by reading the model source). The full
        // table's "Edit" button DOES carry a real data-interval, sourced from the same
        // enrichWithRadar() row. Every zone row is a strict subset of the rows the full table
        // renders (buildUpcomingZone() runs over the identical $rows array actionSubscriptions()
        // assigns to $subscriptions, and only ever drops lapsed/cancelled non-zombie rows —
        // rows that can never also appear in the zone), so a norm_key-keyed lookup against the
        // table's own Edit buttons is structurally guaranteed to resolve. Same technique reused
        // below for the Clear-override copy's manual/detector-backed branch.
        //
        // subIntervalByKey/subManualByKey themselves are built earlier in this function, BEFORE
        // `new DataTable(...)` runs (WR-03 fix, 261-REVIEW.md) — see that comment for why the
        // ordering is load-bearing, not cosmetic.

        const SUB_LITERAL_INTERVALS = ['7', '14', '30', '91', '365'];

        function subTodayLocalYmd(): string {
            // Local (browser) calendar date, not UTC — mirrors invoice.ts's autoCalcDueDate()
            // getFullYear()/getMonth()/getDate() pattern rather than toISOString(), which would
            // shift the date across midnight for any timezone ahead of UTC.
            const d = new Date();
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }

        function subSetAnchorDate(value: string): void {
            if (!subAnchorInput) return;
            // Never write .value directly on a flatpickr-bound input — the calendar UI would
            // keep showing the OLD date until the user opened it (invoice.ts:428-434 pattern).
            const fp = (subAnchorInput as any)._flatpickr;
            if (fp) {
                fp.setDate(value, false);
            } else {
                subAnchorInput.value = value;
            }
        }

        function subHideError(): void {
            if (subErrorEl) subErrorEl.classList.add('d-none');
        }

        function subShowError(msg: string): void {
            if (subErrorEl) {
                subErrorEl.textContent = msg;
                subErrorEl.classList.remove('d-none');
            }
        }

        function subResetNameWarning(): void {
            if (subNameWarning) {
                subNameWarning.textContent = '';
                subNameWarning.classList.add('d-none');
            }
        }

        function subApplyInterval(rawInterval: string): void {
            if (!subIntervalSel || !subYearsWrap || !subYearsInput) return;
            if (SUB_LITERAL_INTERVALS.includes(rawInterval)) {
                subIntervalSel.value = rawInterval;
                subYearsWrap.classList.add('d-none');
                return;
            }
            const parsedDays = parseInt(rawInterval, 10);
            if (rawInterval !== '' && !isNaN(parsedDays) && parsedDays > 0) {
                subIntervalSel.value = 'every_n_years';
                subYearsWrap.classList.remove('d-none');
                subYearsInput.value = String(Math.max(2, Math.round(parsedDays / 365)));
                return;
            }
            // Defensive fallback only — the norm_key-keyed lookup above should make this
            // unreachable (see the handoff comment). If it ever fires, default to Monthly
            // but say so out loud rather than opening the modal silently wrong.
            subIntervalSel.value = '30';
            subYearsWrap.classList.add('d-none');
            subShowError("Couldn't determine the current interval — defaulted to Monthly, please verify before saving.");
        }

        function subApplyRemoveCopy(normKey: string, name: string, isManual: boolean): void {
            if (subRemoveLabel) subRemoveLabel.textContent = isManual ? 'Delete this subscription?' : 'Remove your override?';
            if (subRemoveBody) {
                subRemoveBody.textContent = isManual
                    ? `${name} has no detected charges — this removes it completely and cannot be undone.`
                    : `${name} stays on this page with its detected data. This only clears the date/amount/interval you set — it won't touch its alert history.`;
            }
            if (subRemoveKeyInput) subRemoveKeyInput.value = normKey;
            // Mutually exclusive with the delete-sub (child_id) mode below — clear it so a
            // stale value from a previous open can never route this confirm down the wrong path.
            if (subRemoveChildIdInput) subRemoveChildIdInput.value = '';
        }

        // D-34 sibling-delete extension (261.1-09): a sibling row's Remove item is
        // child_id-keyed (delete-sub) rather than norm_key-keyed (remove-sub) — a split
        // merchant's clearOverride() has no concept of which sibling to act on, so siblings
        // must always route through deleteSubscription()'s server-side re-derived reassignment
        // instead. Reuses #subscriptionRemoveModal's exact prefill-then-show shape.
        function subApplyDeleteCopy(childId: string, name: string): void {
            if (subRemoveLabel) subRemoveLabel.textContent = `Remove ${name}?`;
            if (subRemoveBody) {
                subRemoveBody.textContent = `${name}'s charges move to a remaining sibling on the same merchant, or to Unassigned if this was the last one. This can't be undone.`;
            }
            if (subRemoveKeyInput) subRemoveKeyInput.value = '';
            if (subRemoveChildIdInput) subRemoveChildIdInput.value = childId;
        }

        // D-36 — the Merchant <select> is Choices.js-enhanced by init_choices() (shared.ts, on
        // every page load, no new init call needed). Choices wraps the native <select> in its
        // own overlay UI, so disabling/enabling it must go through the Choices instance's own
        // disable()/enable() methods (util.ts's exported helpers only cover value-setting via
        // setChoiceByValue()/select_choices() — not documented gotcha at util.ts:184-186's
        // scope, but the same "don't fight the wrapper" principle) — merely toggling the
        // underlying <select>.disabled leaves the Choices overlay itself still clickable.
        // window.choices (populated by init_choices()) is the only handle available without
        // adding a new export to util.ts, which is out of this plan's files_modified scope.
        function subGetChoicesInstance(el: HTMLSelectElement | null): any {
            if (!el || !window.choices) return undefined;
            return (window.choices as any[]).find((c: any) => c?.passedElement?.element === el);
        }

        function subSetMerchantSelect(normKey: string, disabled: boolean): void {
            if (!subMerchantSelect) return;
            select_choices(subMerchantSelect, normKey);
            const instance = subGetChoicesInstance(subMerchantSelect);
            if (instance) {
                if (disabled) instance.disable(); else instance.enable();
            } else {
                subMerchantSelect.disabled = disabled;
            }
        }

        function subOpenAddMode(): void {
            if (subModalLabel) subModalLabel.textContent = 'Add subscription';
            subHideError();
            if (subKeyInput) subKeyInput.value = '';
            if (subChildIdInput) subChildIdInput.value = '';
            if (subAddFlagInput) subAddFlagInput.value = '1';
            if (subNameInput) { subNameInput.value = ''; subNameInput.disabled = false; }
            subResetNameWarning();
            subSetMerchantSelect('', false);
            subSetAnchorDate(subTodayLocalYmd());
            subApplyInterval('30');
            if (subAmountInput) subAmountInput.value = '';
            if (subClearBtn) subClearBtn.classList.add('d-none');
        }

        function subOpenOverrideMode(btn: HTMLElement, title: string): void {
            if (subModalLabel) subModalLabel.textContent = title;
            subHideError();
            const normKey = btn.dataset.normKey ?? '';
            if (subKeyInput) subKeyInput.value = normKey;
            if (subChildIdInput) subChildIdInput.value = btn.dataset.childId ?? '';
            if (subAddFlagInput) subAddFlagInput.value = '0';
            if (subNameInput) { subNameInput.value = btn.dataset.name ?? ''; subNameInput.disabled = true; }
            subResetNameWarning();
            // D-36: the Merchant field opens enabled and prefilled to the row's current
            // merchant in Override/Fix-prediction mode — unlike Split mode below, changing it
            // here is exactly how D-41 adoption gets triggered for an existing manual row.
            subSetMerchantSelect(normKey, false);
            subSetAnchorDate(btn.dataset.anchor ?? '');

            let rawInterval = btn.dataset.interval ?? '';
            if (!rawInterval && normKey && subIntervalByKey.has(normKey)) {
                rawInterval = subIntervalByKey.get(normKey) ?? '';
            }
            subApplyInterval(rawInterval);

            if (subAmountInput) subAmountInput.value = btn.dataset.amount ?? '';
            if (subClearBtn) subClearBtn.classList.remove('d-none');
            subEditModal.show();
        }

        // D-34/D-36 — "splitting is simply adding a second subscription pointing at the same
        // merchant" (UI-SPEC Screen Contract §4). Direct sibling of subOpenAddMode(): the
        // Merchant field is pre-filled AND locked to the triggering row's norm_key (you are
        // splitting FROM this merchant, so it cannot change mid-flow), Name starts blank and
        // focused (type the sibling's name), anchor/interval/amount default exactly like Add.
        function subOpenSplitMode(btn: HTMLElement): void {
            if (subModalLabel) subModalLabel.textContent = 'Split into new subscription';
            subHideError();
            if (subKeyInput) subKeyInput.value = '';
            if (subChildIdInput) subChildIdInput.value = '';
            if (subAddFlagInput) subAddFlagInput.value = '1';
            if (subNameInput) { subNameInput.value = ''; subNameInput.disabled = false; }
            subResetNameWarning();
            subSetMerchantSelect(btn.dataset.normKey ?? '', true);
            subSetAnchorDate(subTodayLocalYmd());
            subApplyInterval('30');
            if (subAmountInput) subAmountInput.value = '';
            if (subClearBtn) subClearBtn.classList.add('d-none');
            subEditModal.show();
            if (subNameInput) {
                subEditModalEl!.addEventListener('shown.bs.modal', () => subNameInput!.focus(), { once: true });
            }
        }

        function subOpenRemoveModal(btn: HTMLElement): void {
            const isManual = btn.dataset.manual === '1';
            subApplyRemoveCopy(btn.dataset.normKey ?? '', btn.dataset.name ?? 'this subscription', isManual);
            subRemoveModal.show();
        }

        function subOpenDeleteModal(btn: HTMLElement): void {
            subApplyDeleteCopy(btn.dataset.childId ?? '', btn.dataset.name ?? 'this subscription');
            subRemoveModal.show();
        }

        // D-24 — Dismiss confirm modal, prefilled from the triggering kebab item.
        function subOpenDismissMode(btn: HTMLElement): void {
            const name = btn.dataset.name || 'this subscription';
            if (subDismissBody) {
                subDismissBody.textContent = `${name} won't show up here again — even if it charges again — and won't trigger any alerts. You can undo this any time from the Dismissed list at the bottom of the page.`;
            }
            if (subDismissKeyInput) subDismissKeyInput.value = btn.dataset.normKey ?? '';
            subDismissModal.show();
        }

        // D-26 — Archive confirm modal, prefilled from the triggering kebab item.
        function subOpenArchiveMode(btn: HTMLElement): void {
            const name = btn.dataset.name || 'this subscription';
            if (subArchiveLabel) subArchiveLabel.textContent = `Archive ${name}?`;
            if (subArchiveNormKeyInput) subArchiveNormKeyInput.value = btn.dataset.normKey ?? '';
            if (subArchiveChildIdInput) subArchiveChildIdInput.value = btn.dataset.childId ?? '';
            subArchiveModal.show();
        }

        // Restore (D-24/D-26 reversal) — no confirm, POSTs directly and reloads. The button's
        // own data-url (server-rendered {link undismissSubscription!}/{link
        // unarchiveSubscription!}, same convention as every other signal button on this page)
        // decides the endpoint. Two separate functions, not one shared helper — matches this
        // file's own established fetch() convention (every signal call inlines its own request,
        // e.g. the Save/Remove/Dismiss/Archive fetches above), since undismiss/unarchive carry
        // genuinely distinct POST-field contracts (norm_key vs child_id).
        function subRestoreDismissed(btn: HTMLElement): void {
            const url = btn.dataset.url ?? '';
            if (!url) return;
            const name = btn.dataset.name || 'Subscription';

            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: new URLSearchParams({ norm_key: btn.dataset.normKey ?? '' }).toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    notifier.success(`${data.display_name || name} restored — back on your subscriptions list.`);
                    window.location.reload();
                } else {
                    notifier.alert(data.message ?? "Couldn't restore — check your connection and try again.");
                }
            })
            .catch(() => notifier.alert("Couldn't restore — check your connection and try again."));
        }

        function subRestoreArchived(btn: HTMLElement): void {
            const url = btn.dataset.url ?? '';
            if (!url) return;
            const name = btn.dataset.name || 'Subscription';

            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: new URLSearchParams({ child_id: btn.dataset.childId ?? '' }).toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    notifier.success(`${data.display_name || name} restored — back on your active subscriptions.`);
                    window.location.reload();
                } else {
                    notifier.alert(data.message ?? "Couldn't restore — check your connection and try again.");
                }
            })
            .catch(() => notifier.alert("Couldn't restore — check your connection and try again."));
        }

        // D-39 — Assign a single Unassigned-band charge to the sibling picked in its own
        // <select data-charge-id>. Read the underlying <select>'s .value, not Choices'
        // internal state — Choices.js keeps the native element in sync (UI-SPEC's own
        // instruction), so this needs no Choices-specific accessor.
        function subAssignCharge(btn: HTMLElement): void {
            const url      = btn.dataset.url ?? '';
            const chargeId = btn.dataset.chargeId ?? '';
            if (!url || !chargeId) return;

            const sel = document.querySelector<HTMLSelectElement>(`select[data-charge-id="${chargeId}"]`);
            const childId = sel?.value ?? '';
            if (!childId) {
                notifier.alert('Pick a subscription before assigning.');
                return;
            }

            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: new URLSearchParams({ transaction_id: chargeId, child_id: childId }).toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    notifier.success(`Linked to ${data.display_name} — 1 charge routed.`);
                    window.location.reload();
                } else {
                    notifier.alert(data.message ?? "Couldn't link — check your connection and try again.");
                }
            })
            .catch(() => notifier.alert("Couldn't link — check your connection and try again."));
        }

        // D-37's "or dismiss" half — writes the `0` sentinel via ignoreCharge(), never a
        // delete or mis-assignment.
        function subIgnoreCharge(btn: HTMLElement): void {
            const url      = btn.dataset.url ?? '';
            const chargeId = btn.dataset.chargeId ?? '';
            if (!url || !chargeId) return;

            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: new URLSearchParams({ transaction_id: chargeId }).toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    notifier.success('Marked as not a subscription charge.');
                    window.location.reload();
                } else {
                    notifier.alert(data.message ?? "Couldn't dismiss — check your connection and try again.");
                }
            })
            .catch(() => notifier.alert("Couldn't dismiss — check your connection and try again."));
        }

        // ── Delegated click handler — Add / Edit / Fix prediction / Split / Remove /
        // Delete / Dismiss / Archive / Restore / Assign / Ignore ────────────────────
        // D-28/WR-03: every new data-action registers here (document-level, delegated) — never a
        // per-row addEventListener, since DataTables physically replaces <tbody> on redraw.
        document.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLElement>(
                '[data-action="add-sub"], [data-action="edit-sub"], [data-action="fix-prediction"], ' +
                '[data-action="split-sub"], [data-action="remove-sub"], [data-action="delete-sub"], ' +
                '[data-action="dismiss-sub"], [data-action="archive-sub"], ' +
                '[data-action="restore-dismissed"], [data-action="restore-archived"], ' +
                '[data-action="assign-charge"], [data-action="ignore-charge"]'
            );
            if (!btn) return;

            switch (btn.dataset.action) {
                case 'add-sub':
                    // Declarative data-bs-toggle="modal" on the button already opens it —
                    // only reset/prefill here, do not call .show() a second time.
                    subOpenAddMode();
                    break;
                case 'edit-sub':
                    subOpenOverrideMode(btn, 'Override subscription');
                    break;
                case 'fix-prediction':
                    subOpenOverrideMode(btn, 'Fix prediction');
                    break;
                case 'split-sub':
                    subOpenSplitMode(btn);
                    break;
                case 'remove-sub':
                    subOpenRemoveModal(btn);
                    break;
                case 'delete-sub':
                    subOpenDeleteModal(btn);
                    break;
                case 'dismiss-sub':
                    subOpenDismissMode(btn);
                    break;
                case 'archive-sub':
                    subOpenArchiveMode(btn);
                    break;
                case 'restore-dismissed':
                    subRestoreDismissed(btn);
                    break;
                case 'restore-archived':
                    subRestoreArchived(btn);
                    break;
                case 'assign-charge':
                    subAssignCharge(btn);
                    break;
                case 'ignore-charge':
                    subIgnoreCharge(btn);
                    break;
            }
        });

        // ── Interval select — reveal/hide the "years between charges" input ─────
        if (subIntervalSel) {
            subIntervalSel.addEventListener('change', () => {
                if (!subYearsWrap) return;
                subYearsWrap.classList.toggle('d-none', subIntervalSel.value !== 'every_n_years');
            });
        }

        // ── Name-field blur — Add-mode-only collision reassurance (D-16) ────────
        // Reassurance only, never a submission gate — D-16's design is that a collision
        // merges by construction on the server side.
        if (subNameInput) {
            subNameInput.addEventListener('blur', () => {
                if (subNameInput.disabled) return; // Override mode — name is read-only
                const typed = subNameInput.value.trim().toLowerCase().replace(/\s+/g, ' ');
                if (!typed) { subResetNameWarning(); return; }
                const existingKeys = new Set<string>();
                document.querySelectorAll<HTMLElement>('#subscriptions-table [data-norm-key]').forEach(el => {
                    const nk = el.dataset.normKey;
                    if (nk) existingKeys.add(nk.toLowerCase());
                });
                if (existingKeys.has(typed) && subNameWarning) {
                    subNameWarning.textContent = `This looks like an existing subscription: "${typed}" — adding it here will merge with that entry, not create a duplicate.`;
                    subNameWarning.classList.remove('d-none');
                } else {
                    subResetNameWarning();
                }
            });
        }

        // D-41 — pure-read preview chained after a successful Save whose response reports
        // adopt_candidate. Never trusts anything but the bare child id; the numbers rendered
        // here come straight from previewAdoptHistory!, never round-tripped from the client.
        function subOpenAdoptPreview(childId: number): void {
            const previewUrl = subAdoptModalEl!.dataset.previewUrl ?? '';
            if (!previewUrl) { window.location.reload(); return; }  // defensive — should be unreachable

            fetch(previewUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: new URLSearchParams({ child_id: String(childId) }).toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (!data.success) {
                    // The save already committed — reload so the operator sees the saved row
                    // even though the preview itself failed. Adoption is opt-in, never required.
                    window.location.reload();
                    return;
                }
                if (subAdoptChildIdInput) subAdoptChildIdInput.value = String(childId);
                if (subAdoptBody) {
                    const cadence = data.cadence ?? 'irregular';
                    subAdoptBody.textContent = `${data.merchant_display_name} already has ${data.charge_count} charges going back to ${data.earliest_date}. Linking will adopt this history: ${cadence}, typically $${number_format(data.typical_amount ?? 0, 2)}, last charged ${data.last_charge_date}. The Manual badge will clear.`;
                }
                if (subAdoptUnattributable) {
                    if ((data.unattributable ?? 0) > 0) {
                        subAdoptUnattributable.textContent = `${data.unattributable} of these charges don't clearly match any sibling by amount and will land in Unassigned instead.`;
                        subAdoptUnattributable.classList.remove('d-none');
                    } else {
                        subAdoptUnattributable.textContent = '';
                        subAdoptUnattributable.classList.add('d-none');
                    }
                }
                subAdoptModal.show();
            })
            .catch(() => window.location.reload());
        }

        // ── Save (Add / Override / Split) ────────────────────────────────────────
        if (subSaveBtn) {
            subSaveBtn.addEventListener('click', () => {
                subHideError();

                const typedName  = subNameInput?.value.trim() ?? '';
                const merchant   = subMerchantSelect?.value ?? '';
                const childId    = subChildIdInput?.value ?? '';
                const anchor     = subAnchorInput?.value.trim() ?? '';
                const amountRaw  = subAmountInput?.value ?? '';
                const amount     = parseFloat(amountRaw);
                const intervalSelVal = subIntervalSel?.value ?? '';

                if (!typedName) {
                    subShowError("Couldn't save — enter a name.");
                    return;
                }
                if (!/^\d{4}-\d{2}-\d{2}$/.test(anchor)) {
                    subShowError("Couldn't save — enter a valid anchor date.");
                    return;
                }
                if (isNaN(amount) || amount <= 0) {
                    subShowError("Couldn't save — enter an expected amount.");
                    return;
                }

                let intervalDays: number;
                if (intervalSelVal === 'every_n_years') {
                    const years = parseInt(subYearsInput?.value ?? '', 10);
                    if (isNaN(years) || years < 2) {
                        subShowError("Couldn't save — enter a whole number of years (2 or more).");
                        return;
                    }
                    intervalDays = 365 * years;
                } else {
                    intervalDays = parseInt(intervalSelVal, 10);
                }

                const origHtml = subSaveBtn.innerHTML;
                subSaveBtn.setAttribute('disabled', 'disabled');
                subSaveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

                // D-36 (261.1-08): handleSaveSubscriptionOverride()/saveSubscription() now read
                // Name/Merchant as independent inputs plus a child_id identity — the typed Name
                // is NEVER normalized client- or server-side; only the Merchant picker value
                // (already a known norm_key from the <select>, never free text) reaches
                // RecurringModel::normalizeMerchant(). CR-01 (261-REVIEW.md): X-Requested-With
                // is REQUIRED by requireAjaxPost() — not CORS-safelisted, forces a preflight a
                // cross-origin attacker cannot satisfy.
                fetch(subSaveBtn.dataset.url ?? '', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: new URLSearchParams({
                        name: typedName,
                        merchant: merchant,
                        child_id: childId,
                        anchor_date: anchor,
                        interval_days: String(intervalDays),
                        amount: String(amount),
                    }).toString(),
                })
                .then(r => r.json())
                .then((data: any) => {
                    // Branch on the JSON body's success field — HTTP status is always 200.
                    if (data.success) {
                        subEditModal.hide();
                        if (data.adopt_candidate) {
                            // D-41 — never stack two open Bootstrap modals: the Edit modal is
                            // hidden above BEFORE the Adopt modal is ever shown.
                            subOpenAdoptPreview(data.child_id);
                        } else {
                            window.location.reload();
                        }
                    } else {
                        subSaveBtn.removeAttribute('disabled');
                        subSaveBtn.innerHTML = origHtml;
                        subShowError(data.message ?? 'Save failed.');
                    }
                })
                .catch(() => {
                    subSaveBtn.removeAttribute('disabled');
                    subSaveBtn.innerHTML = origHtml;
                    subShowError("Couldn't save — check your connection and try again.");
                });
            });
        }

        // ── Adopt-history confirm (D-41) ─────────────────────────────────────────
        if (subAdoptConfirmBtn) {
            subAdoptConfirmBtn.addEventListener('click', () => {
                const childId = subAdoptChildIdInput?.value ?? '';
                if (!childId) return;

                const origHtml = subAdoptConfirmBtn.innerHTML;
                subAdoptConfirmBtn.setAttribute('disabled', 'disabled');

                fetch(subAdoptConfirmBtn.dataset.url ?? '', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: new URLSearchParams({ child_id: childId }).toString(),
                })
                .then(r => r.json())
                .then((data: any) => {
                    if (data.success) {
                        notifier.success(`History adopted — ${data.display_name} now has ${data.charge_count} charges dating back to ${data.earliest_date}.`);
                        window.location.reload();
                    } else {
                        subAdoptConfirmBtn.removeAttribute('disabled');
                        subAdoptConfirmBtn.innerHTML = origHtml;
                        notifier.alert(data.message ?? "Couldn't adopt — check your connection and try again.");
                    }
                })
                .catch(() => {
                    subAdoptConfirmBtn.removeAttribute('disabled');
                    subAdoptConfirmBtn.innerHTML = origHtml;
                    notifier.alert("Couldn't adopt — check your connection and try again.");
                });
            });
        }

        // ── Clear override → opens the Remove modal prefilled from the edit modal ──
        if (subClearBtn) {
            subClearBtn.addEventListener('click', () => {
                const normKey  = subKeyInput?.value ?? '';
                const rowName  = subNameInput?.value || 'this subscription';
                const isManual = subManualByKey.get(normKey) ?? false;
                subEditModal.hide();
                subApplyRemoveCopy(normKey, rowName, isManual);
                subRemoveModal.show();
            });
        }

        // ── Remove/clear-override confirm — branches on which of the two mutually
        // exclusive hidden inputs is populated (D-34 sibling-delete extension) ────
        if (subRemoveConfirmBtn) {
            subRemoveConfirmBtn.addEventListener('click', () => {
                const childId = subRemoveChildIdInput?.value ?? '';
                const normKey = subRemoveKeyInput?.value ?? '';
                const isDeleteMode = childId !== '';
                if (!isDeleteMode && !normKey) return;

                const url = isDeleteMode
                    ? (subRemoveConfirmBtn.dataset.deleteUrl ?? '')
                    : (subRemoveConfirmBtn.dataset.url ?? '');
                const body = isDeleteMode
                    ? new URLSearchParams({ child_id: childId })
                    : new URLSearchParams({ norm_key: normKey });

                const origHtml = subRemoveConfirmBtn.innerHTML;
                subRemoveConfirmBtn.setAttribute('disabled', 'disabled');
                subRemoveConfirmBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

                // CR-01 (261-REVIEW.md): X-Requested-With is REQUIRED by
                // TransactionsPresenter::requireAjaxPost() on this signal — see the identical
                // note on the save-override fetch() above.
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: body.toString(),
                })
                .then(r => r.json())
                .then((data: any) => {
                    if (data.success) {
                        subRemoveModal.hide();
                        if (isDeleteMode) {
                            // deleteSubscription()'s reassigned/unassigned counts — the operator
                            // learns what happened to the charges rather than just "deleted".
                            const reassigned = Number(data.reassigned ?? 0);
                            const unassigned = Number(data.unassigned ?? 0);
                            notifier.success(`Removed — ${reassigned} charge${reassigned === 1 ? '' : 's'} reassigned, ${unassigned} moved to Unassigned.`);
                        }
                        window.location.reload();
                    } else {
                        subRemoveConfirmBtn.removeAttribute('disabled');
                        subRemoveConfirmBtn.innerHTML = origHtml;
                        notifier.alert(data.message ?? "Couldn't remove — check your connection and try again.");
                    }
                })
                .catch(() => {
                    subRemoveConfirmBtn.removeAttribute('disabled');
                    subRemoveConfirmBtn.innerHTML = origHtml;
                    notifier.alert("Couldn't remove — check your connection and try again.");
                });
            });
        }

        // ── Dismiss confirm (D-24) ───────────────────────────────────────────────
        if (subDismissConfirmBtn) {
            subDismissConfirmBtn.addEventListener('click', () => {
                const normKey = subDismissKeyInput?.value ?? '';
                if (!normKey) return;

                const origHtml = subDismissConfirmBtn.innerHTML;
                subDismissConfirmBtn.setAttribute('disabled', 'disabled');
                subDismissConfirmBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

                fetch(subDismissConfirmBtn.dataset.url ?? '', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: new URLSearchParams({ norm_key: normKey }).toString(),
                })
                .then(r => r.json())
                .then((data: any) => {
                    if (data.success) {
                        subDismissModal.hide();
                        window.location.reload();
                    } else {
                        subDismissConfirmBtn.removeAttribute('disabled');
                        subDismissConfirmBtn.innerHTML = origHtml;
                        notifier.alert(data.message ?? "Couldn't dismiss — check your connection and try again.");
                    }
                })
                .catch(() => {
                    subDismissConfirmBtn.removeAttribute('disabled');
                    subDismissConfirmBtn.innerHTML = origHtml;
                    notifier.alert("Couldn't dismiss — check your connection and try again.");
                });
            });
        }

        // ── Archive confirm (D-26) ───────────────────────────────────────────────
        if (subArchiveConfirmBtn) {
            subArchiveConfirmBtn.addEventListener('click', () => {
                const normKey = subArchiveNormKeyInput?.value ?? '';
                const childId = subArchiveChildIdInput?.value ?? '';
                if (!normKey && !childId) return;

                const origHtml = subArchiveConfirmBtn.innerHTML;
                subArchiveConfirmBtn.setAttribute('disabled', 'disabled');
                subArchiveConfirmBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

                fetch(subArchiveConfirmBtn.dataset.url ?? '', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: new URLSearchParams({ norm_key: normKey, child_id: childId }).toString(),
                })
                .then(r => r.json())
                .then((data: any) => {
                    if (data.success) {
                        subArchiveModal.hide();
                        window.location.reload();
                    } else {
                        subArchiveConfirmBtn.removeAttribute('disabled');
                        subArchiveConfirmBtn.innerHTML = origHtml;
                        notifier.alert(data.message ?? "Couldn't archive — check your connection and try again.");
                    }
                })
                .catch(() => {
                    subArchiveConfirmBtn.removeAttribute('disabled');
                    subArchiveConfirmBtn.innerHTML = origHtml;
                    notifier.alert("Couldn't archive — check your connection and try again.");
                });
            });
        }

        // ── Inline rename (D-32/D-33) — delegated, since DataTables redraws replace
        // <tbody> rows (WR-03); never a per-row addEventListener. Swaps .js-sub-name in place
        // for a text input inside the same .js-sub-name-wrap container so the row's height
        // never changes. ─────────────────────────────────────────────────────────
        document.addEventListener('click', (e: Event) => {
            const renameBtn = (e.target as HTMLElement).closest<HTMLElement>('.js-sub-rename');
            if (!renameBtn) return;
            subStartRename(renameBtn);
        });

        function subStartRename(renameBtn: HTMLElement): void {
            const wrapper = renameBtn.closest<HTMLElement>('.js-sub-name-wrap');
            const nameEl  = wrapper?.querySelector<HTMLElement>('.js-sub-name') ?? null;
            if (!wrapper || !nameEl) return;
            if (wrapper.querySelector('.js-sub-rename-input')) return;  // already editing

            const currentName = renameBtn.dataset.currentName ?? nameEl.textContent?.trim() ?? '';
            const normKey = renameBtn.dataset.normKey ?? '';
            const childId = renameBtn.dataset.childId ?? '';
            const url     = renameBtn.dataset.url ?? '';

            const input = document.createElement('input');
            input.type = 'text';
            input.className = 'form-control form-control-sm js-sub-rename-input';
            input.value = currentName;

            nameEl.replaceWith(input);
            renameBtn.classList.add('d-none');
            input.focus();
            input.select();

            let committing = false;

            const exitEdit = (): void => {
                if (input.isConnected) input.replaceWith(nameEl);
                renameBtn.classList.remove('d-none');
            };

            const commit = (): void => {
                if (committing) return;
                const newVal = input.value.trim();
                if (newVal === '') {
                    exitEdit();
                    notifier.warning(`Name can't be empty — reverted to "${currentName}".`);
                    return;
                }
                if (newVal === currentName) {
                    // Re-saving the identical string is a silent no-op — no request at all.
                    exitEdit();
                    return;
                }

                committing = true;
                input.disabled = true;

                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: new URLSearchParams({ norm_key: normKey, child_id: childId, display_name: newVal }).toString(),
                })
                .then(r => r.json())
                .then((data: any) => {
                    if (data.success) {
                        const resolvedName = data.display_name || newVal;
                        nameEl.textContent = resolvedName;
                        if (nameEl.hasAttribute('title')) nameEl.setAttribute('title', resolvedName);
                        renameBtn.dataset.currentName = resolvedName;
                        renameBtn.setAttribute('aria-label', `Rename ${resolvedName}`);
                        // Optimistic in-place update, no toast (UI-SPEC's own "the inline
                        // affordance already promises immediacy" — a toast would be redundant).
                        exitEdit();
                    } else {
                        input.disabled = false;
                        committing = false;
                        notifier.alert("Couldn't rename — check your connection and try again.");
                        exitEdit();
                    }
                })
                .catch(() => {
                    input.disabled = false;
                    committing = false;
                    notifier.alert("Couldn't rename — check your connection and try again.");
                    exitEdit();
                });
            };

            input.addEventListener('keydown', (ev: KeyboardEvent) => {
                if (ev.key === 'Enter') {
                    ev.preventDefault();
                    commit();
                } else if (ev.key === 'Escape') {
                    ev.preventDefault();
                    exitEdit();
                }
            });
            input.addEventListener('blur', () => {
                // Guards against a double-submit: Enter's commit() may already be mid-fetch
                // (input still connected until the response resolves) when blur also fires.
                if (input.isConnected && !committing) commit();
            });
        }

        // ── hidden.bs.modal reset (mirrors budgets.ts's _resetModal shape) ───────
        subEditModalEl.addEventListener('hidden.bs.modal', () => {
            subHideError();
            if (subNameInput) subNameInput.disabled = false;
            if (subYearsWrap) subYearsWrap.classList.add('d-none');
            subResetNameWarning();
        });
        subDismissModalEl.addEventListener('hidden.bs.modal', () => {
            if (subDismissKeyInput) subDismissKeyInput.value = '';
        });
        subArchiveModalEl.addEventListener('hidden.bs.modal', () => {
            if (subArchiveNormKeyInput) subArchiveNormKeyInput.value = '';
            if (subArchiveChildIdInput) subArchiveChildIdInput.value = '';
        });
        subAdoptModalEl.addEventListener('hidden.bs.modal', () => {
            if (subAdoptBody) subAdoptBody.textContent = '';
            if (subAdoptUnattributable) {
                subAdoptUnattributable.textContent = '';
                subAdoptUnattributable.classList.add('d-none');
            }
            if (subAdoptChildIdInput) subAdoptChildIdInput.value = '';
        });
    }
}

// ── Merchant deep-dive page init (Phase 156) ─────────────────────────────────
// Guards: only binds when #merchant-trend-chart is in the DOM (project memory:
// index.ts runs all page init()s on every page — guard prevents double-bind on
// other pages: feedback_shared_component_every_page_init_double_bind).
function initMerchant(): void {
    if (!document.getElementById('merchant-trend-chart')) return;  // not on merchant page — no-op

    // Phase 181: theme ternary blocks removed; getChartTheme() supplies all chrome via merge.
    // useUTC already set globally in src/index.ts — no per-chart override (feedback_highcharts_useutc_default)
    // events nest under chart:{} NOT at root level (feedback_highcharts_events_nesting_cast_masks)

    // ── Spline: monthly spend trend (UI-SPEC Component 3) ─────────────────
    const trendMonths  = merchant_trend.map((r: any) => r.month);
    const trendAmounts = merchant_trend.map((r: any) => r.total);
    Highcharts.chart('merchant-trend-chart', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'spline',
            height: 280,
        },
        title: { text: 'Monthly Spend', style: { fontSize: '14px' } },
        xAxis: {
            categories: trendMonths,
            labels: { rotation: -45 },
        },
        yAxis: {
            title: { text: 'Amount ($)' },
            gridLineWidth: 0.1,
        },
        // tooltip.backgroundColor, tooltip.style come from getChartTheme()
        series: [{ type: 'spline', name: 'Spend', data: trendAmounts, color: '#4A90D9' }],  // D-01: identity color fixed
        credits: { enabled: false },
    } as any));

    // ── Donut: category breakdown (UI-SPEC Component 4, mirrors stats() donut) ──
    // Compute total for center-floating subtitle via abbreviateCurrency
    const totalBreakdown = merchant_breakdown.reduce((s: number, d: any) => s + d.total, 0);

    Highcharts.chart('merchant-donut-chart', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'pie',
            height: 350,
        },
        title: { text: 'Spending by Category', style: { fontSize: '14px' } },
        subtitle: {
            text: abbreviateCurrency(totalBreakdown),
            floating: true,
            verticalAlign: 'middle',
            y: 10,
            style: { fontSize: '16px', fontWeight: 'bold' },
        },
        legend: {
            enabled: true,
            layout: 'vertical' as const,
            align: 'right' as const,
            verticalAlign: 'middle' as const,
        },
        plotOptions: {
            pie: {
                innerSize: '60%',
                showInLegend: true,
                dataLabels: { enabled: false },
            },
        },
        series: [{
            type: 'pie',
            name: 'Amount',
            data: merchant_breakdown.map((d: any) => {
                const p: any = { name: d.main_name, y: d.total };
                if (d.main_color) p.color = d.main_color;  // conditional — never explicit undefined (feedback_highcharts_undefined_options)
                return p;
            }),
        }],
        credits: { enabled: false },
    } as any));

    // ── Merchant-scoped server-side DataTable (UI-SPEC Component 6) ─────────
    // Sends merchantKey in POST body — handler reads getPost('merchantKey') (D-09)
    // No self-link on the Name column for the detail page (UI-SPEC §Component 6)
    new DataTable('.datatable[data-table="merchant_list"]', {
        pageLength: 20,
        processing: true,
        serverSide: true,
        ajax: {
            // GET (mirrors the proven main-list table). Everything rides in the query string:
            //  - name=  → satisfies actionMerchant($name), which Nette re-runs on every signal
            //    request to this presenter (a bare '?do=getTransactions' drops it → 404 "Missing
            //    parameter $name");
            //  - draw/start/length/search/order → fill handleGetTransactions() method params,
            //    which Nette fills from QUERY, not POST body (Phase 150/151 lesson);
            //  - merchantKey → read by the handler's getPost() ?? getQuery() fallback (D-09).
            // (Phase 156 UAT fix — POST left draw/start/length unfillable + dropped name.)
            url: '?do=getTransactions&name=' + encodeURIComponent(merchant_key),
            data: function(d: any) {
                d.merchantKey = merchant_key;  // D-09: scope to this merchant (query param under GET)
                return d;
            },
        },
        columns: [
            { data: 'date' },
            { data: 'name' },  // plain text on merchant page — no link back to itself (UI-SPEC §Component 6)
            { data: null, render: function(data: any) { return '$' + number_format(data.amount, 2); } },
            { data: null, render: function(data: any) {
                // Flow badge — D-08 visual distinction for credit/income rows
                if (data.flow === 'debit')   return '<span class="badge bg-danger bg-opacity-10 text-danger">Debit</span>';
                if (data.flow === 'credit')  return '<span class="badge bg-success bg-opacity-10 text-success">Credit</span>';
                if (data.flow === 'income')  return '<span class="badge bg-info bg-opacity-10 text-info">Income</span>';
                return data.flow ?? '';
            }},
            { data: null, render: function(data: any) { return data.group ?? ''; } },
            { data: null, render: function(data: any) { return data.description ?? ''; } },
        ],
        order: [[0, 'desc']] as Order[],
    });
}
